import { useEffect, useMemo, useState, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import { BASE_URL } from "../config";
import { showSuccess, showError, showConfirm } from "../utils/dialog";
import {
  addAppCalendarDays,
  formatAppDate,
  formatAppDateTime,
  formatAppIntlLabel,
  formatAppTime,
  getAppTodayIso,
  getAppWallDateParts,
  getAppWeekStartIso,
  instantFromAppWallParts,
  parseDbDateTime,
  toAppDateKey,
  toMysqlUtcWallString,
} from "../utils/appDateTime";
import {
  buildAvailabilitySlotFromDb,
  buildDbPatternFromSlot,
  dbAvailabilityToMinutes,
  parseTimeInputToMinutes,
} from "../utils/availabilityMinutes";
import DateNavigator from "../tempStaff/components/DateNavigator";

function dateFromIso(iso) {
  return parseDbDateTime(`${iso}T12:00:00Z`) || new Date();
}

function isoFromDate(currentDate) {
  if (!currentDate) return getAppTodayIso();
  if (currentDate instanceof Date) return toAppDateKey(currentDate);
  const parsed = parseDbDateTime(currentDate);
  return parsed ? toAppDateKey(parsed) : String(currentDate).slice(0, 10);
}

/** FullCalendar currentStart is local midnight — use local Y-M-D, not app TZ. */
function isoFromLocalCalendarDate(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function appClockMinutes(instant) {
  const parts = getAppWallDateParts(instant);
  if (!parts) return 0;
  return parts.hour * 60 + parts.minute;
}

function appClockDecimal(instant) {
  const parts = getAppWallDateParts(instant);
  if (!parts) return 0;
  return parts.hour + parts.minute / 60;
}

function toDbWallDateTime(instant) {
  return toMysqlUtcWallString(instant) || "";
}

/** Encode app-wall calendar date + clock as UTC-wall MySQL string (matches shifts). */
function wallDateTimeToDb(isoDate, hour, minute = 0) {
  const match = String(isoDate || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  const instant = instantFromAppWallParts({
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(hour) || 0,
    minute: Number(minute) || 0,
    second: 0,
  });
  return instant ? toMysqlUtcWallString(instant) : "";
}

const getAuthToken = () =>
  sessionStorage.getItem("authToken") ||
  sessionStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("token");

const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function PersonnelTempCalendar({ person }) {
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'calendar'
  const calendarRef = useRef(null);
  const calendarHostRef = useRef(null);
  const [calendarView, setCalendarView] = useState("timeGridWeek");
  const [calendarTitle, setCalendarTitle] = useState("");
  const [calendarWeekStartIso, setCalendarWeekStartIso] = useState(null);
  const [selectedCalendarShift, setSelectedCalendarShift] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedTab, setSelectedTab] = useState("TODAY");

  const load = async ({ soft = false } = {}) => {
    if (!person?.id) return;
    const preservedDate = soft
      ? calendarRef.current?.getApi?.()?.getDate?.()
      : null;
    if (soft) setRefreshing(true);
    else setInitialLoading(true);
    setError("");
    try {
      // Load both shifts and availabilities
      const [shiftsRes, availRes] = await Promise.all([
        fetch(`${BASE_URL}/api/personnel/accounts/${person.id}/calendar`, {
          credentials: "include",
        }),
        fetch(
          `${BASE_URL}/api/personnel/accounts/${person.id}/availabilities`,
          { credentials: "include" },
        ).catch(() => ({
          ok: false,
          json: () => ({ success: false, data: [] }),
        })),
      ]);

      const shiftsData = await shiftsRes.json().catch(() => ({}));
      const availData = await availRes
        .json()
        .catch(() => ({ success: true, data: [] }));

      if (!shiftsRes.ok || !shiftsData.success) {
        throw new Error(shiftsData.message || "Failed to load calendar");
      }

      const shifts = Array.isArray(shiftsData.data) ? shiftsData.data : [];
      const availabilities = Array.isArray(availData.data)
        ? availData.data
        : [];

      // Combine shifts and availabilities
      // Availabilities now include both one-time and recurring patterns from registration
      const combined = [
        ...shifts,
        ...availabilities.map((a) => ({
          ...a,
          shift_id: a.is_recurring
            ? a.availability_id
            : `avail-${a.availability_id}`,
          shift_state: a.type || a.status || (a.is_confirmed ? "Confirmed" : "Unconfirmed"), // Default to Unconfirmed for new availabilities
          client_name: a.is_recurring
            ? "(Recurring Availability)"
            : "(Available)",
          position_name: "—",
          location_name: "—",
          isAvailability: true,
          isRecurring: a.is_recurring || false,
        })),
      ];

      setRows(combined);
      return combined;
    } catch (e) {
      const errorMsg = e.message || "Failed to load calendar";
      setError(errorMsg);
      showError(errorMsg, "Failed to Load Calendar");
    } finally {
      if (soft) {
        setRefreshing(false);
        if (preservedDate) {
          requestAnimationFrame(() => {
            const api = calendarRef.current?.getApi?.();
            if (api) {
              api.gotoDate(preservedDate);
              setCalendarTitle(api.view?.title || "");
              setCalendarView(api.view?.type || "timeGridWeek");
              setCurrentDate(api.getDate ? api.getDate() : preservedDate);
            }
          });
        }
      } else {
        setInitialLoading(false);
      }
    }
  };

  useEffect(() => {
    load({ soft: false });
  }, [person?.id]);

  // FullCalendar often measures 0 size inside nested flex/overflow below desktop.
  useEffect(() => {
    if (initialLoading) return undefined;
    const update = () => {
      const api = calendarRef.current?.getApi?.();
      if (api) api.updateSize();
    };
    const raf = requestAnimationFrame(update);
    const host = calendarHostRef.current;
    let ro;
    if (host && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => update());
      ro.observe(host);
    }
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", update);
      if (ro) ro.disconnect();
    };
  }, [initialLoading, calendarView]);

  // Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showOngoingModal, setShowOngoingModal] = useState(false);
  const [showEditAvailModal, setShowEditAvailModal] = useState(false);

  // ── Week-aware button logic ──────────────────────────────────────────────
  // Derive the Mon–Sun bounds of the currently viewed week.
  const viewedWeekBounds = useMemo(() => {
    const weekStartIso = getAppWeekStartIso(isoFromDate(currentDate || new Date()));
    const weekEndIso = addAppCalendarDays(weekStartIso, 6);
    return {
      weekStart: dateFromIso(weekStartIso),
      weekEnd: dateFromIso(weekEndIso),
      weekStartIso,
      weekEndIso,
    };
  }, [currentDate]);

  // Prefer FullCalendar's visible week (personnel view always mounts FC when person.id is set).
  const activeWeekBounds = useMemo(() => {
    const boundsFromWeekStartIso = (weekStartIso) => {
      const weekEndIso = addAppCalendarDays(weekStartIso, 6);
      return {
        weekStart: dateFromIso(weekStartIso),
        weekEnd: dateFromIso(weekEndIso),
        weekStartIso,
        weekEndIso,
      };
    };

    if (calendarWeekStartIso) {
      return boundsFromWeekStartIso(calendarWeekStartIso);
    }

    const api = calendarRef.current?.getApi?.();
    const viewType = api?.view?.type || calendarView || "";
    const currentStart = api?.view?.currentStart;
    if (
      currentStart &&
      (viewType.includes("Week") || viewType === "dayGridWeek")
    ) {
      const localStartIso = isoFromLocalCalendarDate(currentStart);
      if (localStartIso) {
        return boundsFromWeekStartIso(getAppWeekStartIso(localStartIso));
      }
    }
    return viewedWeekBounds;
  }, [viewedWeekBounds, calendarTitle, calendarView, calendarWeekStartIso]);

  // Unconfirmed availability entries for the viewed week (shown in confirm modal).
  const weekUnconfirmedEntries = useMemo(() => {
    const { weekStartIso, weekEndIso } = activeWeekBounds;
    const inWeek = (instant) => {
      const key = toAppDateKey(instant);
      return !!key && key >= weekStartIso && key <= weekEndIso;
    };
    const pad2 = (n) => String(n).padStart(2, "0");
    const toTimeKey = (instant) => {
      const parts = getAppWallDateParts(instant);
      if (!parts) return "00:00";
      return `${pad2(parts.hour)}:${pad2(parts.minute)}`;
    };
    const toDbDateTimeFromMinutes = (isoDate, minutes) => {
      const total = Number(minutes) || 0;
      const dayDelta = Math.floor(total / 1440);
      const mins = ((total % 1440) + 1440) % 1440;
      const dateKey =
        dayDelta !== 0 ? addAppCalendarDays(isoDate, dayDelta) : isoDate;
      return wallDateTimeToDb(
        dateKey,
        Math.floor(mins / 60),
        mins % 60,
      );
    };
    const toTimeKeyFromMinutes = (minutes) =>
      `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;

    const concrete = [];
    const concreteKeys = new Set();
    const concreteDateKeys = new Set();

    rows.forEach((r) => {
      if (!r.isAvailability || !r.start_date) return;
      const state = String(r.shift_state || r.type || r.status || "").toLowerCase();
      const isDisplayable = !state || state === "unconfirmed" || state === "available" || state === "confirmed" || state === "unavailable";
      if (!isDisplayable) return;
      const start = parseDbDateTime(r.start_date);
      const end = parseDbDateTime(r.end_date);
      if (!inWeek(start)) return;

      concrete.push(r);
      const dateKey = toAppDateKey(start);
      const slotKey = `${dateKey}|${toTimeKey(start)}|${end ? toTimeKey(end) : ""}`;
      concreteKeys.add(slotKey);
      concreteDateKeys.add(dateKey);
    });

    const projected = [];
    rows.forEach((r) => {
      if (!r.isAvailability || !r.isRecurring || r.start_date) return;
      const state = String(r.shift_state || r.type || r.status || "").toLowerCase();
      if (state !== "unconfirmed") return;

      const dayNum = Number(r.day);
      if (!dayNum || dayNum < 1 || dayNum > 7) return;
      const dayOffset = dayNum === 7 ? 6 : dayNum - 1;

      const startMin = dbAvailabilityToMinutes(r.start_minutes ?? r.start ?? 0);
      const endMin = dbAvailabilityToMinutes(r.end_minutes ?? r.end ?? 0);
      const displayEndMin = endMin >= 1440 ? endMin % 1440 : endMin;

      const dayIso = addAppCalendarDays(weekStartIso, dayOffset);
      const endDayIso =
        endMin >= 1440 || endMin <= startMin
          ? addAppCalendarDays(dayIso, 1)
          : dayIso;
      if (concreteDateKeys.has(dayIso)) return;

      projected.push({
        ...r,
        start_date: toDbDateTimeFromMinutes(dayIso, startMin),
        end_date: toDbDateTimeFromMinutes(endDayIso, displayEndMin),
        shift_state: r.shift_state || "Unconfirmed",
        isProjected: true,
      });
    });

    return [...concrete, ...projected].sort((a, b) => {
      const da = parseDbDateTime(a.start_date);
      const db = parseDbDateTime(b.start_date);
      return (da?.getTime() || 0) - (db?.getTime() || 0);
    });
  }, [rows, activeWeekBounds]);

  const computeWeekHasAvailable = (sourceRows, bounds) => {
    const { weekStartIso, weekEndIso } = bounds || {};
    if (!weekStartIso || !weekEndIso) return false;
    const weekAvailRows = (sourceRows || []).filter((r) => {
      if (!r.isAvailability || !r.start_date) return false;
      const key = toAppDateKey(parseDbDateTime(r.start_date));
      return !!key && key >= weekStartIso && key <= weekEndIso;
    });
    if (weekAvailRows.length === 0) return false;
    return weekAvailRows.some((r) => {
      const state = String(r.shift_state || r.type || r.status || "").toLowerCase();
      return state === "available" || state === "confirmed";
    });
  };

  // Show CONFIRM only when the week has no available days.
  // Otherwise show EDIT AVAILABILITY.
  const weekHasAvailable = useMemo(() => {
    return computeWeekHasAvailable(rows, activeWeekBounds);
  }, [rows, activeWeekBounds]);

  const nextUnconfirmedDate = useMemo(() => {
    const candidates = rows
      .filter((r) => r.isAvailability)
      .filter((r) => String(r.shift_state || r.type || r.status || "").toLowerCase() === "unconfirmed")
      .map((r) => parseDbDateTime(r.start_date))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());

    return candidates.length > 0 ? candidates[0] : null;
  }, [rows]);

  const openConfirmAvailabilityModal = () => {
    updateCalendarStateFromApi();
    if (weekUnconfirmedEntries.length === 0 && nextUnconfirmedDate) {
      setCurrentDate(dateFromIso(toAppDateKey(nextUnconfirmedDate)));
      if (selectedTab === "TODAY") setSelectedTab("WEEK");
    }
    setShowConfirmModal(true);
  };

  // Confirm Availability modal states
  const [confirmDates, setConfirmDates] = useState({});

  // Helper to build an "empty" ongoing availability structure
  const createEmptyOngoingAvailability = () => ({
    monday: { enabled: false, times: [{ start: "Anytime", end: "Midnight" }] },
    tuesday: { enabled: false, times: [{ start: "Anytime", end: "Midnight" }] },
    wednesday: { enabled: false, times: [{ start: "Anytime", end: "Midnight" }] },
    thursday: { enabled: false, times: [{ start: "Anytime", end: "Midnight" }] },
    friday: { enabled: false, times: [{ start: "Anytime", end: "Midnight" }] },
    saturday: { enabled: false, times: [{ start: "Anytime", end: "Midnight" }] },
    sunday: { enabled: false, times: [{ start: "Anytime", end: "Midnight" }] },
  });

  // Ongoing Availability modal states (initially empty, then hydrated from registration data)
  const [ongoingAvailability, setOngoingAvailability] = useState(
    createEmptyOngoingAvailability,
  );

  // Load ongoing availability from registration (account_availabilities table)
  useEffect(() => {
    if (!person?.id) return;

    const dayKeyMap = {
      1: "monday",
      2: "tuesday",
      3: "wednesday",
      4: "thursday",
      5: "friday",
      6: "saturday",
      7: "sunday",
    };

    const loadOngoing = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/api/personnel/accounts/${person.id}/account-availabilities`,
          { credentials: "include", headers: getAuthHeaders() },
        );
        const j = await res.json().catch(() => ({ success: false, data: [] }));
        if (!res.ok || !j.success) return;

        const next = createEmptyOngoingAvailability();
        (j.data || []).forEach((row) => {
          const dayKey = dayKeyMap[row.day];
          if (!dayKey || !next[dayKey]) return;

          const slot = buildAvailabilitySlotFromDb(row.start ?? 0, row.end ?? 86340);

          if (!next[dayKey].enabled) {
            next[dayKey].enabled = true;
            next[dayKey].times = [slot];
          } else {
            next[dayKey].times = [...next[dayKey].times, slot];
          }
        });

        setOngoingAvailability(next);
      } catch (err) {
        console.error("Failed to load ongoing availability", err);
      }
    };

    loadOngoing();
  }, [person?.id]);

  const statusColors = {
    Unconfirmed: "#D1D5DB",  // grey  — recurring pattern, not yet confirmed
    unconfirmed: "#D1D5DB",
    Available: "#4ADE80",  // light green — confirmed as available
    available: "#4ADE80",
    Confirmed: "#4ADE80",  // same as Available (legacy type)
    confirmed: "#4ADE80",
    Unavailable: "#FECACA",  // light pink/red — marked unavailable
    unavailable: "#FECACA",
    Scheduled: "#93C5FD",  // light blue — has actual shift
    scheduled: "#93C5FD",
  };

  const statusTextColor = {
    Unconfirmed: "#374151",
    unconfirmed: "#374151",
    Available: "#14532D",
    available: "#14532D",
    Confirmed: "#14532D",
    confirmed: "#14532D",
    Unavailable: "#991B1B",
    unavailable: "#991B1B",
    Scheduled: "#1E3A5F",
    scheduled: "#1E3A5F",
  };

  // Get unique dates that have shifts (for quick navigation)
  const datesWithShifts = useMemo(() => {
    return [
      ...new Set(
        rows
          .map((shift) => {
            if (!shift.start_date) return null;
            return toAppDateKey(parseDbDateTime(shift.start_date));
          })
          .filter(Boolean),
      ),
    ].sort();
  }, [rows]);

  // Map each date → unique statuses present, sorted by priority (highest first)
  const STATUS_PRIORITY = {
    Scheduled: 4, scheduled: 4,
    Available: 3, available: 3,
    Confirmed: 3, confirmed: 3,  // legacy — same visual as Available
    Unavailable: 2, unavailable: 2,
    Unconfirmed: 1, unconfirmed: 1,
  };

  const dateStatusMap = useMemo(() => {
    const map = {};
    const addKey = (dateKey, state) => {
      if (!dateKey || !state) return;
      if (!map[dateKey]) map[dateKey] = new Set();
      map[dateKey].add(state);
    };
    rows.forEach((shift) => {
      if (!shift.start_date) return;
      const startKey = toAppDateKey(parseDbDateTime(shift.start_date));
      addKey(startKey, shift.shift_state);
      if (shift.end_date) {
        const endKey = toAppDateKey(parseDbDateTime(shift.end_date));
        if (endKey && endKey !== startKey) {
          addKey(endKey, shift.shift_state);
        }
      }
    });
    // Convert sets to arrays sorted by priority desc
    const result = {};
    Object.entries(map).forEach(([k, set]) => {
      result[k] = [...set].sort((a, b) => (STATUS_PRIORITY[b] || 0) - (STATUS_PRIORITY[a] || 0));
    });
    return result;
  }, [rows]);

  // Helper: render up to 4 tiny coloured status dots for a date key
  const StatusDots = ({ dateKey, maxDots = 4 }) => {
    const statuses = dateStatusMap[dateKey] || [];
    if (statuses.length === 0) return null;
    const shown = statuses.slice(0, maxDots);
    return (
      <div className="flex flex-wrap gap-[3px] justify-center mt-1">
        {shown.map((s, i) => (
          <span
            key={i}
            className="w-2 h-2 rounded-full inline-block flex-shrink-0"
            style={{ backgroundColor: statusColors[s] || "#9CA3AF" }}
            title={s}
          />
        ))}
        {statuses.length > maxDots && (
          <span className="text-[9px] text-gray-400 leading-none">+{statuses.length - maxDots}</span>
        )}
      </div>
    );
  };

  // Quick jump to next date with shifts
  const jumpToNextShift = () => {
    if (datesWithShifts.length === 0) return;
    const currentStr = isoFromDate(currentDate || new Date());
    const nextDate =
      datesWithShifts.find((d) => d > currentStr) || datesWithShifts[0];
    if (nextDate) {
      setCurrentDate(dateFromIso(nextDate));
      setSelectedTab("DAY");
    }
  };

  const timeToDecimal = (timeStr) => {
    const [hour, min] = timeStr.split(":").map(Number);
    return hour + min / 60;
  };

  const formatDateTime = (dateTime) =>
    formatAppDateTime(dateTime, { emptyValue: "—" });

  const formatDateTimeCompact = (dateTime) => {
    if (!dateTime) return "—";
    return `${formatAppDate(dateTime)} ${formatAppTime(dateTime)}`;
  };

  const getStatusColor = (status) => {
    const statusLower = (status || "").toLowerCase();
    switch (statusLower) {
      case "scheduled":
        return "text-sky-700 bg-sky-100 border border-sky-200";
      case "available":
        return "text-green-700 bg-green-100 border border-green-200";
      case "unavailable":
        return "text-gray-700 bg-gray-100 border border-gray-200";
      case "unconfirmed":
        return "text-yellow-700 bg-yellow-100 border border-yellow-200";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  // Get date range description for current filter
  const getDateRangeText = () => {
    const selectedDate = currentDate || new Date();

    switch (selectedTab) {
      case "TODAY":
        return `Today (${formatAppDate(new Date())})`;
      case "DAY":
        return formatAppIntlLabel(dateFromIso(isoFromDate(selectedDate)), {
          weekday: "long",
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      case "WEEK": {
        const weekStartIso = getAppWeekStartIso(isoFromDate(selectedDate));
        const weekEndIso = addAppCalendarDays(weekStartIso, 6);
        const startLabel = formatAppIntlLabel(dateFromIso(weekStartIso), {
          month: "short",
          day: "numeric",
        });
        const endLabel = formatAppIntlLabel(dateFromIso(weekEndIso), {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        return `Week of ${startLabel} - ${endLabel}`;
      }
      case "MONTH":
        return formatAppIntlLabel(dateFromIso(isoFromDate(selectedDate)), {
          month: "long",
          year: "numeric",
        });
      default:
        return "";
    }
  };

  // Filter shifts based on selected tab (works in both list and calendar views)
  const filteredShifts = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const todayIso = getAppTodayIso();
    const selectedIso = isoFromDate(currentDate || new Date());

    const filtered = rows.filter((shift) => {
      if (!shift.start_date) return false;
      const shiftStartKey = toAppDateKey(parseDbDateTime(shift.start_date));
      const shiftEndKey = shift.end_date
        ? toAppDateKey(parseDbDateTime(shift.end_date))
        : "";
      if (!shiftStartKey) return false;

      const isOvernight = !!shiftEndKey && shiftEndKey !== shiftStartKey;

      switch (selectedTab) {
        case "TODAY":
          return (
            shiftStartKey === todayIso ||
            (isOvernight && shiftEndKey === todayIso)
          );

        case "DAY":
          return (
            shiftStartKey === selectedIso ||
            (isOvernight && shiftEndKey === selectedIso)
          );

        case "WEEK": {
          const weekStartIso = getAppWeekStartIso(selectedIso);
          const weekEndIso = addAppCalendarDays(weekStartIso, 6);
          const inWeekByStart =
            shiftStartKey >= weekStartIso && shiftStartKey <= weekEndIso;
          const inWeekByEnd =
            isOvernight &&
            shiftEndKey >= weekStartIso &&
            shiftEndKey <= weekEndIso;
          return inWeekByStart || inWeekByEnd;
        }

        case "MONTH": {
          const [selY, selM] = selectedIso.split("-").map(Number);
          const [shiftY, shiftM] = shiftStartKey.split("-").map(Number);
          return shiftY === selY && shiftM === selM;
        }

        default:
          return true;
      }
    });

    return filtered;
  }, [rows, selectedTab, currentDate]);

  const generateMonthDays = (year, month) => {
    const days = [];
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const startDayIndex = (firstDay.getDay() + 6) % 7; // convert Sunday=0 → 6 for Monday start
    const totalDays = lastDay.getDate();

    // Fill empty cells before month starts
    for (let i = 0; i < startDayIndex; i++) days.push(null);

    // Fill days of the month
    for (let d = 1; d <= totalDays; d++) days.push(d);

    // Fill remaining empty cells to complete the final week (so total = multiple of 7)
    while (days.length % 7 !== 0) days.push(null);

    return days;
  };

  const calendarEventTypes = [
    { value: "unconfirmed", name: "Unconfirmed", color: "#d1d1d1", textColor: "#111827" },
    { value: "available", name: "Available", color: "#7eec6b", textColor: "#111827" },
    { value: "unavailable", name: "Unavailable", color: "#ffe5e4", textColor: "#111827" },
    { value: "scheduled", name: "Scheduled", color: "#719bff", textColor: "#ffffff" },
  ];
  const calendarEventTypeByValue = calendarEventTypes.reduce((acc, item) => {
    acc[item.value] = item;
    return acc;
  }, {});
  const calendarViewOptions = [
    { label: "List", value: "listWeek" },
    { label: "Day", value: "timeGridDay" },
    { label: "Week", value: "timeGridWeek" },
    { label: "Month", value: "dayGridMonth" },
  ];

  const normalizeCalendarEventType = (row) => {
    if (!row?.isAvailability) return "scheduled";
    const raw = String(row.shift_state || row.type || row.status || "").trim().toLowerCase();
    if (raw === "confirmed") return "available";
    if (raw === "available" || raw === "unavailable" || raw === "unconfirmed") return raw;
    return "unconfirmed";
  };

  const calendarEvents = useMemo(() => {
    return rows
      .map((row) => {
        const start = parseDbDateTime(row.start_date);
        const end = parseDbDateTime(row.end_date);
        if (!start || !end) return null;

        const type = normalizeCalendarEventType(row);
        const config = calendarEventTypeByValue[type] || calendarEventTypeByValue.unconfirmed;
        const isScheduled = type === "scheduled";
        const title = isScheduled
          ? row.client_name || row.position_name || "Scheduled"
          : config.name;

        return {
          id: String(row.shift_id || row.availability_id || `${type}-${start.getTime()}`),
          title,
          start,
          end,
          display: isScheduled ? "auto" : "background",
          backgroundColor: config.color,
          borderColor: config.color,
          textColor: config.textColor,
          classNames: [`atlas-calendar-event-${type}`],
          extendedProps: {
            type,
            row,
          },
        };
      })
      .filter(Boolean);
  }, [rows]);

  const updateCalendarStateFromApi = () => {
    const api = calendarRef.current?.getApi?.();
    if (!api) return;
    setCalendarTitle(api.view?.title || "");
    setCalendarView(api.view?.type || "timeGridWeek");
    const viewType = api.view?.type || "";
    const currentStart = api.view?.currentStart;
    if (
      currentStart &&
      (viewType.includes("Week") || viewType === "dayGridWeek")
    ) {
      const localIso = isoFromLocalCalendarDate(currentStart);
      if (localIso) {
        setCalendarWeekStartIso(getAppWeekStartIso(localIso));
      }
      setCurrentDate(currentStart);
      return;
    }
    setCurrentDate(api.getDate?.() || new Date());
  };

  const changeCalendarView = (view) => {
    const api = calendarRef.current?.getApi?.();
    if (!api) {
      setCalendarView(view);
      return;
    }
    api.changeView(view);
    updateCalendarStateFromApi();
  };

  const navigateCalendar = (action) => {
    const api = calendarRef.current?.getApi?.();
    if (!api) return;
    if (action === "today") api.today();
    if (action === "prev") api.prev();
    if (action === "next") api.next();
    updateCalendarStateFromApi();
  };

  const renderCalendarEventContent = (eventInfo) => {
    const row = eventInfo.event.extendedProps?.row || {};
    const type = eventInfo.event.extendedProps?.type;
    if (type !== "scheduled") return null;

    const isMonth = eventInfo.view.type === "dayGridMonth";
    return (
      <div className="min-w-0 leading-tight">
        {!isMonth && row.position_name ? (
          <div className="truncate">{row.position_name}</div>
        ) : null}
        <div className={isMonth ? "truncate" : "truncate mt-1"}>
          {row.client_name || "Scheduled"}
        </div>
      </div>
    );
  };

  const formatCalendarDateShort = (date) => {
    if (!date) return "";
    const monthDay = formatAppDate(date, { emptyValue: "" });
    const time = formatAppTime(date, { emptyValue: "" })
      .replace(":00", "")
      .toLowerCase();
    return `${monthDay}, ${time}`;
  };

  const formatCalendarDetailRange = (startValue, endValue) => {
    const start = parseDbDateTime(startValue);
    const end = parseDbDateTime(endValue);
    if (!start || !end) return "—";
    return `${formatCalendarDateShort(start)} – ${formatCalendarDateShort(end)}`;
  };

  const formatCalendarDuration = (startValue, endValue) => {
    const start = parseDbDateTime(startValue);
    const end = parseDbDateTime(endValue);
    if (!start || !end) return "";
    const minutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    if (hours && !remainder) return `${hours}h`;
    if (hours && remainder) return `${hours}h ${remainder}m`;
    return `${remainder}m`;
  };

  const formatCalendarStatus = (value) => {
    const normalized = String(value || "").replace(/[_-]+/g, " ").trim();
    if (!normalized) return "—";
    return normalized.replace(/\b\w/g, (m) => m.toUpperCase());
  };

  const getCalendarShiftStatus = (row) =>
    row?.calendar_status || row?.assignment_ui_status || row?.shift_state;

  const formatCalendarPayRate = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return "—";
    return `$${n.toFixed(2)}/h`;
  };

  const formatCalendarLocation = (row) =>
    [
      row?.location_name,
      row?.location_street,
      row?.location_address2,
      row?.location_city,
      row?.location_region,
      row?.location_zip,
      row?.location_country,
    ]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .filter((part, idx, arr) => arr.indexOf(part) === idx)
      .join(", ");

  if (person?.id) {
    return (
      <div className="flex flex-col min-h-0 w-full lg:h-full">
        <style>
          {`
            .atlas-personnel-calendar {
              -webkit-overflow-scrolling: touch;
              overscroll-behavior-x: contain;
              width: 100%;
            }
            .atlas-personnel-calendar .personnel-fc-canvas {
              min-width: 920px;
              width: 920px;
              height: 620px;
            }
            .atlas-personnel-calendar .personnel-fc-canvas .fc {
              width: 100% !important;
              height: 100% !important;
              font-size: 13px;
            }
            /* Personnel.css stacks all tables below 640px; keep FullCalendar as real tables.
               Only undo Personnel.css's *cosmetic* label-stacking styles here (display,
               padding, text-align, margin, border) -- width and position are left alone
               because FullCalendar's own CSS (.fc-scrollgrid table { width:100%;
               table-layout:fixed }) and its absolutely-positioned event layer depend on
               them; forcing width:auto/position:static back onto FullCalendar's own
               table/td elements broke its day-column layout on mobile. */
            @media (max-width: 640px) {
              .atlas-personnel-calendar table,
              .atlas-personnel-calendar thead,
              .atlas-personnel-calendar tbody,
              .atlas-personnel-calendar tr,
              .atlas-personnel-calendar th,
              .atlas-personnel-calendar td {
                display: revert !important;
                padding-left: 0 !important;
                text-align: inherit !important;
                margin-bottom: 0 !important;
                border-bottom: none !important;
              }
              .atlas-personnel-calendar table thead {
                display: table-header-group !important;
              }
              .atlas-personnel-calendar td::before {
                content: none !important;
              }
            }
            .atlas-personnel-calendar .fc a {
              color: initial;
            }
            .atlas-personnel-calendar .fc-event.atlas-calendar-event-scheduled {
              cursor: pointer;
            }
            .atlas-personnel-calendar .fc-daygrid-dot-event.atlas-calendar-event-scheduled {
              align-items: stretch;
              background: #719bff;
              border-radius: 2px;
              color: #ffffff;
              display: block;
              margin: 1px 2px;
              padding: 2px 4px;
              width: calc(100% - 4px);
            }
            .atlas-personnel-calendar .fc-daygrid-dot-event.atlas-calendar-event-scheduled .fc-daygrid-event-dot {
              display: none;
            }
            .atlas-personnel-calendar .fc-daygrid-dot-event.atlas-calendar-event-scheduled .fc-event-title,
            .atlas-personnel-calendar .fc-daygrid-dot-event.atlas-calendar-event-scheduled .fc-event-main {
              color: #ffffff;
              min-width: 0;
              width: 100%;
            }
            .atlas-personnel-calendar .fc-daygrid-dot-event.atlas-calendar-event-scheduled:hover {
              background: #5f8df0;
            }
            .atlas-personnel-calendar .fc-list-event-title,
            .atlas-personnel-calendar .fc-list-event-time {
              cursor: pointer;
            }
            .atlas-personnel-calendar .fc-list-event-graphic {
              display: none;
            }
            .atlas-personnel-calendar .fc-list-day-text {
              float: left;
            }
            .atlas-personnel-calendar .fc-list-day-side-text {
              float: right;
            }
            .atlas-personnel-calendar .fc-bg-event {
              opacity: 1;
            }
            @media (min-width: 1024px) {
              .atlas-personnel-calendar .personnel-fc-canvas {
                min-width: 100%;
                width: 100%;
                height: 620px;
              }
            }
          `}
        </style>

        {/* Below lg, avoid nested h-full so tablet/mobile can scroll and show the calendar canvas. */}
        <div className="space-y-4 pb-4 min-h-0 w-full lg:flex-1 lg:overflow-y-auto">
          {error && <div className="text-sm text-red-600">{error}</div>}

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium hover:bg-gray-50"
                onClick={() => navigateCalendar("today")}
              >
                Today
              </button>
              <button
                type="button"
                className="w-8 h-8 rounded-full text-red-600 hover:bg-red-50 flex items-center justify-center"
                onClick={() => navigateCalendar("prev")}
                aria-label="Previous"
              >
                ‹
              </button>
              <div className="text-base font-medium text-gray-800 min-w-0 sm:min-w-[160px] text-center flex-1 sm:flex-none">
                {calendarTitle}
              </div>
              <button
                type="button"
                className="w-8 h-8 rounded-full text-red-600 hover:bg-red-50 flex items-center justify-center"
                onClick={() => navigateCalendar("next")}
                aria-label="Next"
              >
                ›
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {calendarViewOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => changeCalendarView(option.value)}
                  className={`px-3 py-2 rounded-md border text-sm font-medium ${calendarView === option.value
                      ? "bg-red-600 border-red-600 text-white"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
            {calendarEventTypes.map((item) => (
              <div key={item.value} className="flex items-center gap-1.5 whitespace-nowrap">
                <span
                  className="w-4 h-4 rounded-sm inline-block"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-gray-700">{item.name}</span>
              </div>
            ))}
          </div>

          <div
            ref={calendarHostRef}
            className="atlas-personnel-calendar border border-gray-200 rounded bg-white p-2 overflow-x-auto relative"
          >
            {initialLoading ? (
              <div className="text-sm text-gray-500 p-4 min-h-[620px]">Loading…</div>
            ) : (
              <>
                {refreshing && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 text-sm text-gray-600">
                    Refreshing…
                  </div>
                )}
                <div
                  className="personnel-fc-canvas"
                  style={{ minWidth: 920, width: 920, height: 620 }}
                >
                  <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
                    initialView="timeGridWeek"
                    initialDate={currentDate}
                    headerToolbar={false}
                    height={620}
                    allDaySlot={false}
                    firstDay={1}
                    events={calendarEvents}
                    eventContent={renderCalendarEventContent}
                    eventClick={(info) => {
                      const type = info.event.extendedProps?.type;
                      if (type === "scheduled") {
                        setSelectedCalendarShift(info.event.extendedProps?.row || null);
                      }
                    }}
                    datesSet={(info) => {
                      setCalendarTitle(info.view?.title || "");
                      setCalendarView(info.view?.type || "timeGridWeek");
                      const viewType = info.view?.type || "";
                      const localStartIso = isoFromLocalCalendarDate(
                        info.view?.currentStart,
                      );
                      if (
                        localStartIso &&
                        (viewType.includes("Week") || viewType === "dayGridWeek")
                      ) {
                        setCalendarWeekStartIso(getAppWeekStartIso(localStartIso));
                      }
                      const anchor =
                        viewType.includes("Week") || viewType === "dayGridWeek"
                          ? info.view?.currentStart
                          : calendarRef.current?.getApi?.()?.getDate?.() ||
                          info.view?.currentStart;
                      setCurrentDate(anchor || new Date());
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-wrap justify-center items-center gap-4 py-3 px-4 border-t border-gray-200 bg-white">
          {weekHasAvailable ? (
            <button
              type="button"
              onClick={() => setShowEditAvailModal(true)}
              className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-semibold text-sm transition-colors"
            >
              EDIT AVAILABILITY
            </button>
          ) : (
            <button
              type="button"
              onClick={openConfirmAvailabilityModal}
              className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-semibold text-sm transition-colors"
            >
              CONFIRM AVAILABILITY
            </button>
          )}

          <button
            type="button"
            onClick={() => setShowOngoingModal(true)}
            className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-semibold text-sm transition-colors"
          >
            CHANGE ONGOING AVAILABILITY
          </button>
        </div>

        {selectedCalendarShift && (
          <div
            className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/40 p-4"
            onClick={() => setSelectedCalendarShift(null)}
          >
            <div
              className="bg-white rounded-sm shadow-2xl w-full max-w-[470px] p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-normal text-gray-800 mb-6">Shift Details</h3>
              <div className="grid grid-cols-2 gap-x-12 gap-y-8 text-sm">
                <div>
                  <div className="text-xs text-gray-400 mb-2">Status</div>
                  <div className="text-gray-700">
                    {formatCalendarStatus(getCalendarShiftStatus(selectedCalendarShift))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-2">Date and Time</div>
                  <div className="text-gray-700 leading-tight">
                    {formatCalendarDetailRange(
                      selectedCalendarShift.start_date,
                      selectedCalendarShift.end_date,
                    )}
                  </div>
                  <div className="text-gray-500 mt-1">
                    {formatCalendarDuration(
                      selectedCalendarShift.start_date,
                      selectedCalendarShift.end_date,
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-2">Client</div>
                  <div className="text-gray-700">{selectedCalendarShift.client_name || "—"}</div>
                  {(selectedCalendarShift.supervisor_name || selectedCalendarShift.contact_name) && (
                    <div className="text-xs text-gray-500 mt-1 leading-tight">
                      Supervisor:{" "}
                      {selectedCalendarShift.supervisor_name ||
                        selectedCalendarShift.contact_name}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-3">Location</div>
                  <div className="text-gray-700 leading-tight">
                    {formatCalendarLocation(selectedCalendarShift) || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-2">Position and Pay Rate</div>
                  <div className="text-gray-700">{selectedCalendarShift.position_name || "—"}</div>
                  <div className="text-gray-700">
                    {formatCalendarPayRate(selectedCalendarShift.pay_rate)}
                  </div>
                </div>
              </div>
              <div className="mt-10">
                <button
                  type="button"
                  className="text-red-600 text-sm font-medium uppercase hover:underline"
                  onClick={() => setSelectedCalendarShift(null)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {showConfirmModal && (
          <ConfirmAvailabilityModal
            person={person}
            weekBounds={activeWeekBounds}
            unconfirmedEntries={weekUnconfirmedEntries}
            onClose={() => setShowConfirmModal(false)}
            onSave={async (entries) => {
              try {
                const res = await fetch(
                  `${BASE_URL}/api/personnel/accounts/${person.id}/confirm-availability`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ entries }),
                  },
                );
                const j = await res.json();
                if (!res.ok || !j.success) {
                  throw new Error(j.message || "Failed to confirm availability");
                }
                const confirmedCount = entries.filter(e => e.type === "Available").length;
                showSuccess(
                  `Week confirmed: ${confirmedCount} slot(s) marked Available.`,
                  "Success",
                );
                setShowConfirmModal(false);
                const nextRows = await load({ soft: true });
                const shouldShowEdit = computeWeekHasAvailable(
                  Array.isArray(nextRows) ? nextRows : rows,
                  activeWeekBounds,
                );
                setShowEditAvailModal(shouldShowEdit);
              } catch (err) {
                showError(err.message || "Failed to confirm availability", "Error");
              }
            }}
          />
        )}

        {showOngoingModal && (
          <OngoingAvailabilityModal
            person={person}
            availability={ongoingAvailability}
            onClose={() => setShowOngoingModal(false)}
            onSave={async (availability) => {
              try {
                const patterns = [];
                const dayMap = {
                  monday: 1,
                  tuesday: 2,
                  wednesday: 3,
                  thursday: 4,
                  friday: 5,
                  saturday: 6,
                  sunday: 7,
                };

                for (const [dayKey, dayData] of Object.entries(availability)) {
                  if (!dayData.enabled) continue;
                  const dayNum = dayMap[dayKey];

                  for (const timeSlot of dayData.times) {
                    patterns.push(buildDbPatternFromSlot(dayNum, timeSlot));
                  }
                }

                const res = await fetch(
                  `${BASE_URL}/api/personnel/accounts/${person.id}/account-availabilities`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ patterns }),
                  },
                );
                const j = await res.json();
                if (!res.ok || !j.success) {
                  throw new Error(j.message || "Failed to save ongoing availability");
                }
                showSuccess("Ongoing availability updated successfully!", "Success");
                setShowOngoingModal(false);
                setOngoingAvailability(availability);
                await load({ soft: true });
              } catch (err) {
                showError(err.message || "Failed to save ongoing availability", "Error");
              }
            }}
          />
        )}

        {showEditAvailModal && (
          <EditAvailabilityModal
            person={person}
            rows={rows}
            weekBounds={activeWeekBounds}
            onClose={() => setShowEditAvailModal(false)}
            onSaved={() => {
              load({ soft: true });
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 space-y-4 pb-4">
        <div className="text-sm font-semibold text-gray-700">
          🗓️ Calendar for {person?.name || `#${person?.id}`}
        </div>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <div>
              {filteredShifts.length} shift
              {filteredShifts.length !== 1 ? "s" : ""}
              {filteredShifts.length !== rows.length && ` (${rows.length} total)`}
            </div>
            <div className="text-xs text-gray-500 mt-1">{getDateRangeText()}</div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode("list")}
              className={`px-4 py-2 text-sm font-medium rounded ${viewMode === "list"
                  ? "bg-red-600 text-white"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
            >
              LIST
            </button>
            <button
              onClick={() => setViewMode("calendar")}
              className={`px-4 py-2 text-sm font-medium rounded ${viewMode === "calendar"
                  ? "bg-red-600 text-white"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
            >
              CALENDAR
            </button>
          </div>
        </div>
        <div className="flex space-x-4 mb-4 border-b border-border">
          {["TODAY", "DAY", "WEEK", "MONTH"].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className={`pb-2 px-1 text-sm text-[#DC1F00] font-medium border-b-2 transition-colors ${selectedTab === tab
                  ? "border-[#DC1F00] text-sidebar-active"
                  : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="flex-1 min-w-0">
            {/* Hide date navigator for TODAY tab since it always shows today's date */}
            {selectedTab !== "TODAY" && (
              <DateNavigator
                activeTab={selectedTab}
                currentDate={currentDate}
                setCurrentDate={setCurrentDate}
              />
            )}

            {/* Mini week-strip for DAY tab: shows Mon–Sun of the current week with status dots */}
            {(selectedTab === "DAY" || selectedTab === "TODAY") && (() => {
              const base = selectedTab === "TODAY" ? new Date() : currentDate;
              const weekStartIso = getAppWeekStartIso(isoFromDate(base));
              const weekDays = Array.from({ length: 7 }, (_, idx) =>
                dateFromIso(addAppCalendarDays(weekStartIso, idx)),
              );
              const todayIso = getAppTodayIso();
              return (
                <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
                  {weekDays.map((d) => {
                    const key = toAppDateKey(d);
                    const isToday = key === todayIso;
                    const isActive =
                      selectedTab !== "TODAY" && key === isoFromDate(currentDate);
                    const hasData = !!dateStatusMap[key];
                    const dayNum = getAppWallDateParts(d)?.day;
                    return (
                      <button
                        key={key}
                        onClick={() => {
                          setCurrentDate(dateFromIso(key));
                          if (selectedTab === "TODAY") setSelectedTab("DAY");
                        }}
                        className={`flex flex-col items-center px-2 py-1 rounded-lg min-w-[44px] transition-colors ${isActive
                            ? "bg-gray-800 text-white"
                            : isToday
                              ? "bg-red-50 border border-red-200"
                              : "hover:bg-gray-100"
                          }`}
                        title={formatAppIntlLabel(d, {
                          weekday: "long",
                          month: "short",
                          day: "numeric",
                        })}
                      >
                        <span className={`text-[10px] font-medium uppercase tracking-wide ${isActive ? "text-gray-300" : "text-gray-500"}`}>
                          {formatAppIntlLabel(d, { weekday: "short" }).substring(0, 3)}
                        </span>
                        <span className={`text-sm font-bold ${isActive ? "text-white" : isToday ? "text-red-600" : "text-gray-800"}`}>
                          {dayNum}
                        </span>
                        <div className="flex gap-[2px] justify-center mt-0.5 min-h-[8px]">
                          {(dateStatusMap[key] || []).slice(0, 3).map((s, i) => (
                            <span
                              key={i}
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: statusColors[s] || "#9CA3AF" }}
                            />
                          ))}
                          {!hasData && <span className="w-1.5 h-1.5" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })()}

            <div className="flex flex-wrap gap-x-4 gap-y-1 pb-1">
              {[
                { name: "Unconfirmed", color: "#D1D5DB", text: "#374151" },
                { name: "Available", color: "#4ADE80", text: "#14532D" },
                { name: "Unavailable", color: "#FECACA", text: "#991B1B" },
                { name: "Scheduled", color: "#93C5FD", text: "#1E3A5F" },
              ].map((item) => (
                <div
                  key={item.name}
                  className="flex items-center gap-1.5 whitespace-nowrap"
                >
                  <div
                    className="w-4 h-4 rounded flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-xs font-medium" style={{ color: item.text }}>{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {initialLoading || refreshing ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded p-6 text-gray-400 text-center">
            No shifts scheduled
          </div>
        ) : viewMode === "list" ? (
          filteredShifts.length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded p-6 text-gray-400 text-center">
              <div className="text-lg mb-2">No shifts found</div>
              <div className="text-sm">
                No shifts scheduled for{" "}
                <strong className="text-gray-600">{getDateRangeText()}</strong>
              </div>
              {datesWithShifts.length > 0 && (
                <div className="mt-4">
                  <button
                    onClick={jumpToNextShift}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium"
                  >
                    Jump to Next Shift
                  </button>
                  <div className="text-xs mt-2 text-gray-500">
                    You have shifts on:{" "}
                    {datesWithShifts
                      .slice(0, 3)
                      .map((d) =>
                        formatAppIntlLabel(dateFromIso(d), {
                          month: "short",
                          day: "numeric",
                        }),
                      )
                      .join(", ")}
                    {datesWithShifts.length > 3 &&
                      ` and ${datesWithShifts.length - 3} more dates`}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="border border-gray-200 rounded overflow-hidden bg-white shadow-sm">
                <table className="w-full table-auto text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-2 py-2">Client</th>
                      <th className="text-left px-2 py-2">Position</th>
                      <th className="text-left px-2 py-2">Location</th>
                      <th className="text-left px-2 py-2 whitespace-nowrap">Start Date</th>
                      <th className="text-left px-2 py-2 whitespace-nowrap">End Date</th>
                      <th className="text-left px-2 py-2 whitespace-nowrap">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredShifts.map((r) => (
                      <tr
                        key={r.shift_id}
                        className="border-t border-gray-200 hover:bg-gray-50"
                      >
                        <td className="px-2 py-2 align-top break-words">{r.client_name || ""}</td>
                        <td className="px-2 py-2 align-top break-words">{r.position_name || "—"}</td>
                        <td className="px-2 py-2 align-top break-words">{r.location_name || "—"}</td>
                        <td className="px-2 py-2 align-top whitespace-nowrap text-xs leading-5">
                          {formatDateTimeCompact(r.start_date)}
                        </td>
                        <td className="px-2 py-2 align-top whitespace-nowrap text-xs leading-5">
                          {formatDateTimeCompact(r.end_date)}
                        </td>
                        <td className="px-2 py-2 align-top">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                              r.shift_state,
                            )}`}
                          >
                            {r.shift_state
                              ? r.shift_state.charAt(0).toUpperCase() +
                              r.shift_state.slice(1).toLowerCase()
                              : "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ) : null}

        {/* Calendar View Implementations */}
        {viewMode === "calendar" && (
          <>
            {(selectedTab === "DAY" || selectedTab === "TODAY") && (
              <div className="mt-2">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th></th>
                      <th className="border-l border-gray-400 text-center px-2 pb-2 text-sm text-gray-600">
                        {formatAppIntlLabel(
                          selectedTab === "TODAY"
                            ? new Date()
                            : dateFromIso(isoFromDate(currentDate)),
                          {
                            weekday: "long",
                            month: "short",
                            day: "numeric",
                          },
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 25 }).map((_, i) => {
                      const label =
                        i === 0
                          ? "12:00 AM"
                          : i < 12
                            ? `${i}:00 AM`
                            : i === 12
                              ? "12:00 PM"
                              : i < 24
                                ? `${i - 12}:00 PM`
                                : "12:00 AM";

                      // Midnight boundary row — closing line only
                      if (i === 24) {
                        return (
                          <tr key={24}>
                            <td className="w-20 px-2 py-0 text-sm text-gray-600 align-top">{label}</td>
                            <td className="border-l border-t border-gray-400 h-1" />
                          </tr>
                        );
                      }

                      return (
                        <tr key={i}>
                          <td className="w-20 h-16 px-2 text-sm text-gray-600 align-top">
                            {label}
                          </td>
                          <td className="border-l border-t border-gray-400 h-16 relative">
                            {filteredShifts.map((shift, idx) => {
                              if (!shift.start_date || !shift.end_date)
                                return null;

                              const startDateTime = parseDbDateTime(shift.start_date);
                              const endDateTime = parseDbDateTime(shift.end_date);
                              if (!startDateTime || !endDateTime) return null;

                              const viewDateKey =
                                selectedTab === "TODAY"
                                  ? getAppTodayIso()
                                  : isoFromDate(currentDate);
                              const startKey = toAppDateKey(startDateTime);
                              const endKey = toAppDateKey(endDateTime);
                              const startClockMin = appClockMinutes(startDateTime);
                              const endClockMin = appClockMinutes(endDateTime);
                              const effectiveEndKey =
                                startKey === endKey && endClockMin <= startClockMin
                                  ? addAppCalendarDays(endKey, 1)
                                  : endKey;

                              const startsOnViewDay = startKey === viewDateKey;
                              const endsOnViewDay = effectiveEndKey === viewDateKey;
                              const isOvernightContinuation =
                                !startsOnViewDay && endsOnViewDay;

                              if (!startsOnViewDay && !isOvernightContinuation)
                                return null;

                              let startHour, endHour;
                              if (isOvernightContinuation) {
                                startHour = 0;
                                endHour = appClockDecimal(endDateTime);
                              } else {
                                startHour = appClockDecimal(startDateTime);
                                const endsNextDay = effectiveEndKey !== startKey;
                                endHour = endsNextDay
                                  ? 24
                                  : appClockDecimal(endDateTime);
                              }

                              const color =
                                statusColors[shift.shift_state] || "#999";
                              const tc =
                                statusTextColor[shift.shift_state] || "#1F2937";

                              // Skip if this hour is outside the shift time
                              if (i + 1 <= startHour || i >= endHour) return null;

                              // Calculate positioning within the hour cell
                              const top =
                                i <= startHour ? (startHour - i) * 100 : 0;
                              const bottom =
                                i + 1 >= endHour ? (i + 1 - endHour) * 100 : 0;
                              const height = 100 - top - bottom;

                              return (
                                <div
                                  key={idx}
                                  className="absolute left-0 w-full flex items-center justify-center overflow-hidden"
                                  style={{
                                    top: `${top}%`,
                                    height: `${height}%`,
                                    backgroundColor: color,
                                  }}
                                  title={`${shift.client_name} - ${shift.position_name}\n${formatDateTime(shift.start_date)} - ${formatDateTime(shift.end_date)}${isOvernightContinuation ? "\n(Continued from previous day)" : ""}`}
                                >
                                  {height > 22 && (
                                    <span className="text-xs font-semibold px-1 truncate" style={{ color: tc }}>
                                      {shift.shift_state || shift.client_name}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* WEEK tab implementation */}
            {selectedTab === "WEEK" && (
              <div className="mt-2 overflow-x-auto">
                <table className="w-full border-collapse table-fixed min-w-[800px]">
                  <thead>
                    <tr>
                      <th className="w-20"></th>
                      {[
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                        "Sunday",
                      ].map((day, index) => {
                        const weekStartIso = getAppWeekStartIso(isoFromDate(currentDate));
                        const colKey = addAppCalendarDays(weekStartIso, index);
                        const dayDate = dateFromIso(colKey);
                        const isToday = colKey === getAppTodayIso();
                        const dayNum = getAppWallDateParts(dayDate)?.day;

                        return (
                          <th
                            key={day}
                            className="border-l border-gray-400 text-center px-2 pb-2 text-sm text-gray-600 w-[14.28%]"
                          >
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="font-medium text-xs text-gray-500 uppercase tracking-wide">
                                {day.substring(0, 3)}
                              </span>
                              <span
                                className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold ${isToday
                                    ? "bg-red-600 text-white"
                                    : "text-gray-700"
                                  }`}
                              >
                                {dayNum}
                              </span>
                              <StatusDots dateKey={colKey} maxDots={4} />
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 25 }).map((_, i) => {
                      const label =
                        i === 0
                          ? "12AM"
                          : i < 12
                            ? `${i}AM`
                            : i === 12
                              ? "12PM"
                              : i < 24
                                ? `${i - 12}PM`
                                : "12AM";

                      // Midnight boundary row — closing line only
                      if (i === 24) {
                        return (
                          <tr key={24}>
                            <td className="w-20 px-2 py-0 text-xs text-gray-600 align-top">{label}</td>
                            {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                              <td key={d} className="border-l border-t border-gray-400 h-1" />
                            ))}
                          </tr>
                        );
                      }

                      return (
                        <tr key={i}>
                          <td className="w-20 h-12 px-2 text-xs text-gray-600 align-top">
                            {label}
                          </td>

                          {[0, 1, 2, 3, 4, 5, 6].map((dayOffset) => {
                            const weekStartIso = getAppWeekStartIso(isoFromDate(currentDate));
                            const dayKey = addAppCalendarDays(weekStartIso, dayOffset);

                            return (
                              <td
                                key={dayOffset}
                                className="border-l border-t border-gray-400 h-12 relative"
                              >
                                {filteredShifts.map((shift, idx) => {
                                  if (!shift.start_date || !shift.end_date)
                                    return null;

                                  const shiftStart = parseDbDateTime(shift.start_date);
                                  const shiftEnd = parseDbDateTime(shift.end_date);
                                  if (!shiftStart || !shiftEnd) return null;

                                  const startKey = toAppDateKey(shiftStart);
                                  const endKey = toAppDateKey(shiftEnd);
                                  const startClockMin = appClockMinutes(shiftStart);
                                  const endClockMin = appClockMinutes(shiftEnd);
                                  const effectiveEndKey =
                                    startKey === endKey && endClockMin <= startClockMin
                                      ? addAppCalendarDays(endKey, 1)
                                      : endKey;

                                  const startsOnThisDay = startKey === dayKey;
                                  const endsOnThisDay = effectiveEndKey === dayKey;
                                  const isOvernightContinuation =
                                    !startsOnThisDay && endsOnThisDay;

                                  if (!startsOnThisDay && !isOvernightContinuation)
                                    return null;

                                  let startHour, endHour;
                                  if (isOvernightContinuation) {
                                    startHour = 0;
                                    endHour = appClockDecimal(shiftEnd);
                                  } else {
                                    startHour = appClockDecimal(shiftStart);
                                    const endsNextDay = effectiveEndKey !== startKey;
                                    endHour = endsNextDay
                                      ? 24
                                      : appClockDecimal(shiftEnd);
                                  }

                                  const color =
                                    statusColors[shift.shift_state] || "#999";
                                  const tc =
                                    statusTextColor[shift.shift_state] || "#1F2937";

                                  // Skip if this hour is outside the shift
                                  if (i + 1 <= startHour || i >= endHour)
                                    return null;

                                  const top =
                                    i <= startHour ? (startHour - i) * 100 : 0;
                                  const bottom =
                                    i + 1 >= endHour
                                      ? (i + 1 - endHour) * 100
                                      : 0;
                                  const height = 100 - top - bottom;

                                  return (
                                    <div
                                      key={idx}
                                      className="absolute left-0 w-full flex items-center justify-center overflow-hidden"
                                      style={{
                                        top: `${top}%`,
                                        height: `${height}%`,
                                        backgroundColor: color,
                                      }}
                                      title={`${shift.client_name} - ${shift.position_name}${isOvernightContinuation ? "\n(Continued from previous day)" : ""}`}
                                    >
                                      {height > 22 && (
                                        <span className="text-xs font-semibold px-1 truncate" style={{ color: tc }}>
                                          {shift.shift_state}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* MONTH tab implementation */}
            {selectedTab === "MONTH" && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[800px] border-collapse table-fixed">
                  <thead>
                    <tr>
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                        (day) => (
                          <th
                            key={day}
                            className="border border-gray-300 text-center text-sm text-gray-600 py-2 w-[14.28%]"
                          >
                            {day}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const navIso = isoFromDate(currentDate);
                      const navParts = getAppWallDateParts(
                        parseDbDateTime(`${navIso}T12:00:00Z`),
                      );
                      const year = navParts?.year ?? currentDate.getFullYear();
                      const month = (navParts?.month ?? currentDate.getMonth() + 1) - 1;
                      const days = generateMonthDays(year, month);
                      const todayIso = getAppTodayIso();
                      const selectedIso = isoFromDate(currentDate);

                      const weeks = [];
                      for (let i = 0; i < days.length; i += 7) {
                        weeks.push(days.slice(i, i + 7));
                      }

                      return weeks.map((week, wIdx) => (
                        <tr key={wIdx}>
                          {week.map((day, dIdx) => {
                            if (!day) {
                              return (
                                <td
                                  key={dIdx}
                                  className="border border-gray-300 h-24 bg-gray-50"
                                />
                              );
                            }

                            // Construct date string
                            const formattedDate = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

                            // Find all shifts for this date
                            const dayShifts = filteredShifts.filter((shift) => {
                              if (!shift.start_date) return false;
                              return toAppDateKey(parseDbDateTime(shift.start_date)) === formattedDate;
                            });

                            const isToday = formattedDate === todayIso;
                            const isSelected = formattedDate === selectedIso;
                            const hasActivity = dayShifts.length > 0;

                            return (
                              <td
                                key={dIdx}
                                className={`border h-24 text-sm relative transition-all cursor-pointer select-none ${hasActivity
                                    ? "border-gray-200 hover:bg-gray-50"
                                    : "border-gray-100 bg-gray-50/40"
                                  }`}
                                title={
                                  hasActivity
                                    ? `${dayShifts.length} entry${dayShifts.length > 1 ? "s" : ""} — click to view`
                                    : ""
                                }
                                onClick={() => {
                                  if (hasActivity) {
                                    setCurrentDate(dateFromIso(formattedDate));
                                    setSelectedTab("DAY");
                                  }
                                }}
                              >
                                <div className="flex flex-col items-center pt-2 px-1">
                                  {/* Date number with circle highlight */}
                                  <span
                                    className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold transition-colors ${isToday
                                        ? "bg-red-600 text-white"
                                        : isSelected && hasActivity
                                          ? "bg-gray-800 text-white"
                                          : hasActivity
                                            ? "text-gray-800"
                                            : "text-gray-400"
                                      }`}
                                  >
                                    {day}
                                  </span>

                                  {/* Colored status dots */}
                                  <StatusDots dateKey={formattedDate} maxDots={5} />

                                  {/* Entry count badge */}
                                  {hasActivity && (
                                    <span className="text-[10px] text-gray-400 mt-0.5">
                                      {dayShifts.length} entry{dayShifts.length > 1 ? "s" : ""}
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

      </div>{/* end scrollable area */}

      {/* Action Buttons - pinned at the bottom, never overlaps content */}
      <div className="flex-shrink-0 flex justify-center items-center gap-4 py-3 px-4 border-t border-gray-200 bg-white">
        {weekHasAvailable ? (
          <button
            onClick={() => setShowEditAvailModal(true)}
            className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-semibold text-sm transition-colors"
          >
            EDIT AVAILABILITY
          </button>
        ) : (
          <button
            onClick={openConfirmAvailabilityModal}
            className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-semibold text-sm transition-colors"
          >
            CONFIRM AVAILABILITY
          </button>
        )}

        <button
          onClick={() => setShowOngoingModal(true)}
          className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-semibold text-sm transition-colors"
        >
          CHANGE ONGOING AVAILABILITY
        </button>
      </div>

      {/* Confirm Availability Modal */}
      {showConfirmModal && (
        <ConfirmAvailabilityModal
          person={person}
          weekBounds={activeWeekBounds}
          unconfirmedEntries={weekUnconfirmedEntries}
          onClose={() => setShowConfirmModal(false)}
          onSave={async (entries) => {
            try {
              const res = await fetch(
                `${BASE_URL}/api/personnel/accounts/${person.id}/confirm-availability`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({ entries }),
                },
              );
              const j = await res.json();
              if (!res.ok || !j.success) {
                throw new Error(j.message || "Failed to confirm availability");
              }
              const confirmedCount = entries.filter(e => e.type === "Available").length;
              showSuccess(
                `Week confirmed: ${confirmedCount} slot(s) marked Available.`,
                "Success",
              );
              setShowConfirmModal(false);
              const nextRows = await load({ soft: true });
              const shouldShowEdit = computeWeekHasAvailable(
                Array.isArray(nextRows) ? nextRows : rows,
                activeWeekBounds
              );
              setShowEditAvailModal(shouldShowEdit);
            } catch (err) {
              showError(err.message || "Failed to confirm availability", "Error");
            }
          }}
        />
      )}

      {/* Ongoing Availability Modal */}
      {showOngoingModal && (
        <OngoingAvailabilityModal
          person={person}
          availability={ongoingAvailability}
          onClose={() => setShowOngoingModal(false)}
          onSave={async (availability) => {
            try {
              // Convert availability object to patterns array
              const patterns = [];
              const dayMap = {
                monday: 1,
                tuesday: 2,
                wednesday: 3,
                thursday: 4,
                friday: 5,
                saturday: 6,
                sunday: 7,
              };

              for (const [dayKey, dayData] of Object.entries(availability)) {
                if (!dayData.enabled) continue;
                const dayNum = dayMap[dayKey];

                for (const timeSlot of dayData.times) {
                  patterns.push(buildDbPatternFromSlot(dayNum, timeSlot));
                }
              }

              const res = await fetch(
                `${BASE_URL}/api/personnel/accounts/${person.id}/account-availabilities`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({ patterns }),
                },
              );
              const j = await res.json();
              if (!res.ok || !j.success) {
                throw new Error(
                  j.message || "Failed to save ongoing availability",
                );
              }
              showSuccess(
                "Ongoing availability updated successfully!",
                "Success",
              );
              setShowOngoingModal(false);
              setOngoingAvailability(availability);
            } catch (err) {
              showError(
                err.message || "Failed to save ongoing availability",
                "Error",
              );
            }
          }}
        />
      )}

      {/* Edit Availability Modal */}
      {showEditAvailModal && (
        <EditAvailabilityModal
          person={person}
          rows={rows}
          weekBounds={activeWeekBounds}
          onClose={() => setShowEditAvailModal(false)}
          onSaved={() => {
            load({ soft: true });
          }}
        />
      )}
    </div>
  );
}

// ─── Date+Time Picker Panel ───────────────────────────────────────────────────
// Matches the screenshot: left = month calendar, right = hour + minute grids.
function DateTimePickerPanel({ value, onChange, mode, allowedRange, onContinue, onDone, onBack }) {
  const init = value || new Date();
  const initParts = getAppWallDateParts(init) || {
    year: init.getFullYear(),
    month: init.getMonth() + 1,
    day: init.getDate(),
    hour: init.getHours(),
    minute: init.getMinutes(),
  };
  const [viewYear, setViewYear] = useState(initParts.year);
  const [viewMonth, setViewMonth] = useState(initParts.month - 1);
  const [selDay, setSelDay] = useState(initParts.day);
  const [selHour, setSelHour] = useState(initParts.hour);
  const [selMin, setSelMin] = useState(
    Math.round(initParts.minute / 5) * 5 % 60
  );

  const buildDate = () =>
    instantFromAppWallParts({
      year: viewYear,
      month: viewMonth + 1,
      day: selDay,
      hour: selHour,
      minute: selMin,
      second: 0,
    }) || new Date(viewYear, viewMonth, selDay, selHour, selMin, 0);

  // ── Week-range constraint helpers ────────────────────────────────────────────
  const rangeStart = allowedRange?.start ? new Date(allowedRange.start) : null;
  const rangeEnd = allowedRange?.end ? new Date(allowedRange.end) : null;

  const isDisabledDay = (d) => {
    if (!d || !rangeStart || !rangeEnd) return false;
    const dt = new Date(viewYear, viewMonth, d);
    const rs = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
    const re = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate());
    return dt < rs || dt > re;
  };

  const canGoPrev = () => {
    if (!rangeStart) return true;
    const prevLast = new Date(viewYear, viewMonth, 0);
    return prevLast >= new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
  };
  const canGoNext = () => {
    if (!rangeEnd) return true;
    const nextFirst = new Date(viewYear, viewMonth + 1, 1);
    return nextFirst <= new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate());
  };
  // ─────────────────────────────────────────────────────────────────────────────

  const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
  const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  // Hours: 24 entries, displayed as "12am", "1am", … "11pm"
  const HOURS = Array.from({ length: 24 }, (_, h) => {
    const ampm = h >= 12 ? "pm" : "am";
    const h12 = h % 12 || 12;
    return { label: `${h12}${ampm}`, value: h };
  });

  // Calendar grid (Mon=0 … Sun=6)
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const rawFirst = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
  const firstOffset = rawFirst === 0 ? 6 : rawFirst - 1;        // shift to Mon=0
  const cells = [
    ...Array(firstOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const prevMonth = () => {
    if (!canGoPrev()) return;
    if (viewMonth === 0) { setViewYear((y) => y - 1); setViewMonth(11); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (!canGoNext()) return;
    if (viewMonth === 11) { setViewYear((y) => y + 1); setViewMonth(0); }
    else setViewMonth((m) => m + 1);
  };

  const todayIso = getAppTodayIso();
  const cellIso = (d) =>
    `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const isToday = (d) => !!d && cellIso(d) === todayIso;

  const fmtTime = () => {
    const ampm = selHour >= 12 ? "PM" : "AM";
    const h12 = selHour % 12 || 12;
    return `${h12}:${String(selMin).padStart(2, "0")} ${ampm}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-2xl shadow-2xl p-6 mx-4" style={{ minWidth: 660 }}>
        <div className="flex gap-8">

          {/* ── Left: calendar ── */}
          <div style={{ minWidth: 260 }}>
            {/* Month nav */}
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-gray-900 text-base">
                {MONTH_NAMES[viewMonth]} {viewYear} ▾
              </span>
              <div className="flex gap-1">
                <button
                  onClick={prevMonth}
                  disabled={!canGoPrev()}
                  className={`p-1 rounded text-lg leading-none ${canGoPrev() ? "hover:bg-gray-100 text-gray-600" : "text-gray-300 cursor-not-allowed"}`}
                >‹</button>
                <button
                  onClick={nextMonth}
                  disabled={!canGoNext()}
                  className={`p-1 rounded text-lg leading-none ${canGoNext() ? "hover:bg-gray-100 text-gray-600" : "text-gray-300 cursor-not-allowed"}`}
                >›</button>
              </div>
            </div>
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-1">
              {DAY_LABELS.map((d) => (
                <div key={d} className="text-center text-[10px] text-gray-400 font-semibold py-1 tracking-wide">{d}</div>
              ))}
            </div>
            {/* Date cells */}
            <div className="grid grid-cols-7 gap-y-1">
              {cells.map((d, i) => {
                const outOfRange = isDisabledDay(d);
                const disabled = !d || outOfRange;
                return (
                  <button
                    key={i}
                    disabled={disabled}
                    onClick={() => !disabled && setSelDay(d)}
                    className={`w-8 h-8 mx-auto flex items-center justify-center text-sm rounded-full transition-colors
                      ${!d ? "invisible" : ""}
                      ${!outOfRange && d === selDay
                        ? "bg-red-600 text-white font-bold"
                        : !outOfRange && isToday(d)
                          ? "text-red-600 font-bold hover:bg-gray-100"
                          : outOfRange && d
                            ? "text-gray-300 cursor-not-allowed line-through"
                            : "text-gray-700 hover:bg-gray-100"}
                    `}
                  >
                    {d || ""}
                  </button>
                );
              })}
            </div>
            {/* Week range hint */}
            {rangeStart && rangeEnd && (
              <p className="text-[10px] text-gray-400 text-center mt-2">
                {formatAppIntlLabel(rangeStart, { month: "short", day: "numeric" })}
                {" – "}
                {formatAppIntlLabel(rangeEnd, { month: "short", day: "numeric" })}
                {" only"}
              </p>
            )}
          </div>

          {/* ── Right: time picker ── */}
          <div className="flex-1">
            {/* Current time display */}
            <div className="text-right text-xl font-semibold text-gray-900 mb-3">{fmtTime()}</div>

            <div className="flex gap-4">
              {/* Hours */}
              <div className="flex-1">
                <div className="text-[10px] text-gray-400 uppercase tracking-widest text-center mb-2">Hour</div>
                <div className="grid grid-cols-4 gap-1">
                  {HOURS.map((h) => (
                    <button
                      key={h.value}
                      onClick={() => setSelHour(h.value)}
                      className={`h-8 text-xs rounded-full flex items-center justify-center transition-colors
                        ${h.value === selHour
                          ? "bg-red-600 text-white font-bold"
                          : "text-gray-700 hover:bg-gray-100"}
                      `}
                    >
                      {h.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Minutes */}
              <div style={{ minWidth: 80 }}>
                <div className="text-[10px] text-gray-400 uppercase tracking-widest text-center mb-2">Minute</div>
                <div className="grid grid-cols-2 gap-1">
                  {MINUTES.map((m) => (
                    <button
                      key={m}
                      onClick={() => setSelMin(m)}
                      className={`h-8 w-9 text-xs rounded-full flex items-center justify-center transition-colors
                        ${m === selMin
                          ? "bg-red-600 text-white font-bold"
                          : "text-gray-700 hover:bg-gray-100"}
                      `}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
          {mode === "from" ? (
            <>
              <button
                onClick={() => { onChange(buildDate()); onContinue(buildDate()); }}
                className="text-red-600 font-bold text-sm tracking-wide"
              >
                CONTINUE
              </button>
              <button
                onClick={() => { onChange(buildDate()); onDone(buildDate()); }}
                className="font-bold text-sm text-gray-800 tracking-wide"
              >
                DONE
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => { onChange(buildDate()); onDone(buildDate()); }}
                className="text-red-600 font-bold text-sm tracking-wide"
              >
                DONE
              </button>
              <button onClick={onBack} className="font-bold text-sm text-gray-800 tracking-wide">
                BACK
              </button>
            </>
          )}
          <button className="font-bold text-sm text-gray-400 tracking-wide">MORE</button>
        </div>
      </div>
    </div>
  );
}

// ─── Create Availability Modal ────────────────────────────────────────────────
// Shows From Date + To Date fields. Clicking each opens DateTimePickerPanel.
// On save, generates one availability entry per day in the date range.
function CreateAvailabilityModal({ person, weekBounds, onClose, onSaved }) {
  const [fromDT, setFromDT] = useState(null);   // Date object (date + time)
  const [toDT, setToDT] = useState(null);   // Date object (date + time)
  const [status, setStatus] = useState("Available");
  const [activePicker, setActivePicker] = useState(null); // 'from' | 'to' | null
  const [saving, setSaving] = useState(false);

  const CalIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-gray-400 shrink-0">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );

  const fmtDT = (dt) => {
    if (!dt) return null;
    return formatAppDateTime(dt, { emptyValue: "" });
  };

  const timeStr = (dt) =>
    dt ? formatAppTime(dt, { emptyValue: "9:00 AM" }) : "9:00 AM";

  const handleSave = async () => {
    if (!fromDT) { showError("Please select a From Date", "Validation"); return; }
    if (!toDT) { showError("Please select a To Date", "Validation"); return; }
    if (fromDT > toDT) { showError("From Date must be before To Date", "Validation"); return; }

    setSaving(true);
    try {
      const fromParts = getAppWallDateParts(fromDT);
      const toParts = getAppWallDateParts(toDT);
      if (!fromParts || !toParts) {
        throw new Error("Invalid date selection");
      }
      const entries = [];
      let curIso = toAppDateKey(fromDT);
      const endIso = toAppDateKey(toDT);
      const startMin = fromParts.hour * 60 + fromParts.minute;
      const endMin = toParts.hour * 60 + toParts.minute;

      while (curIso <= endIso) {
        const endDateKey =
          endMin <= startMin ? addAppCalendarDays(curIso, 1) : curIso;
        entries.push({
          start_date: wallDateTimeToDb(curIso, fromParts.hour, fromParts.minute),
          end_date: wallDateTimeToDb(endDateKey, toParts.hour, toParts.minute),
          type: status,
        });
        curIso = addAppCalendarDays(curIso, 1);
      }

      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/confirm-availability`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: JSON.stringify({ entries }),
        }
      );
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error(j.message || "Failed to add");
      showSuccess(
        `Availability added for ${entries.length} day(s). Staff has been notified!`,
        "Success"
      );
      onSaved();
    } catch (err) {
      showError(err.message || "Failed to save availability", "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60]">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Create Date Availability</h3>

          {/* From Date */}
          <div className="mb-1">
            <span className="text-xs text-gray-500">From Date*</span>
            <button
              onClick={() => setActivePicker("from")}
              className="w-full flex items-center gap-3 border-b border-gray-300 py-2 text-left group"
            >
              <CalIcon />
              {fromDT
                ? <span className="flex-1 text-sm text-gray-800">{fmtDT(fromDT)}</span>
                : <span className="flex-1 text-sm text-gray-400">From Date*</span>
              }
              {fromDT && (
                <span
                  onClick={(e) => { e.stopPropagation(); setFromDT(null); }}
                  className="text-gray-400 hover:text-gray-700 text-base leading-none"
                >×</span>
              )}
            </button>
          </div>

          <div className="text-sm text-gray-500 px-1 py-2">to</div>

          {/* To Date */}
          <div className="mb-6">
            <span className="text-xs text-gray-500">To Date*</span>
            <button
              onClick={() => setActivePicker("to")}
              className="w-full flex items-center gap-3 border-b border-gray-300 py-2 text-left group"
            >
              <CalIcon />
              {toDT
                ? <span className="flex-1 text-sm text-gray-800">{fmtDT(toDT)}</span>
                : <span className="flex-1 text-sm text-gray-400">To Date*</span>
              }
              {toDT && (
                <span
                  onClick={(e) => { e.stopPropagation(); setToDT(null); }}
                  className="text-gray-400 hover:text-gray-700 text-base leading-none"
                >×</span>
              )}
            </button>
          </div>

          {/* Status */}
          <div className="mb-8">
            <p className="text-xs text-gray-500 mb-2">Status*</p>
            <div className="flex items-center gap-6">
              {["Available", "Unavailable"].map((opt) => (
                <label key={opt} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ca-status"
                    value={opt}
                    checked={status === opt}
                    onChange={() => setStatus(opt)}
                    className="w-4 h-4 accent-red-600"
                  />
                  <span className="text-sm text-gray-700">{opt}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-6">
            <button
              onClick={handleSave}
              disabled={saving || !fromDT || !toDT}
              className={`text-sm font-bold tracking-wide transition-colors
                ${saving || !fromDT || !toDT ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-700"}`}
            >
              {saving ? "SAVING…" : "CREATE"}
            </button>
            <button
              onClick={onClose}
              className="text-sm font-bold tracking-wide text-gray-800 hover:text-gray-600 transition-colors"
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>

      {/* Date+Time Picker */}
      {activePicker === "from" && (
        <DateTimePickerPanel
          value={fromDT || (weekBounds?.weekStart ? new Date(weekBounds.weekStart) : new Date())}
          onChange={setFromDT}
          mode="from"
          allowedRange={weekBounds ? { start: weekBounds.weekStart, end: weekBounds.weekEnd } : null}
          onContinue={(d) => { setFromDT(d); setActivePicker("to"); }}
          onDone={(d) => { setFromDT(d); setActivePicker(null); }}
          onBack={() => setActivePicker(null)}
        />
      )}
      {activePicker === "to" && (
        <DateTimePickerPanel
          value={toDT || fromDT || (weekBounds?.weekStart ? new Date(weekBounds.weekStart) : new Date())}
          onChange={setToDT}
          mode="to"
          allowedRange={weekBounds ? { start: weekBounds.weekStart, end: weekBounds.weekEnd } : null}
          onContinue={(d) => { setToDT(d); setActivePicker(null); }}
          onDone={(d) => { setToDT(d); setActivePicker(null); }}
          onBack={() => setActivePicker("from")}
        />
      )}
    </>
  );
}

// ─── Edit Availability Modal ──────────────────────────────────────────────────
function EditAvailabilityModal({ person, rows, weekBounds, onClose, onSaved }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editEntry, setEditEntry] = useState(null); // entry being edited

  const sortEntriesOldestFirst = (list) =>
    [...(Array.isArray(list) ? list : [])].sort((a, b) => {
      const aTime = parseDbDateTime(a?.start_date)?.getTime() || 0;
      const bTime = parseDbDateTime(b?.start_date)?.getTime() || 0;
      return aTime - bTime;
    });

  // Filter to current week's one-time entries only
  const inWeek = (dateStr) => {
    if (!weekBounds?.weekStartIso || !dateStr) return !weekBounds;
    const key = toAppDateKey(parseDbDateTime(dateStr));
    return (
      !!key &&
      key >= weekBounds.weekStartIso &&
      key <= weekBounds.weekEndIso
    );
  };

  // ── load entries from parent rows (refreshed via onSaved) ─────────────────
  useEffect(() => {
    const oneTime = (Array.isArray(rows) ? rows : [])
      .filter((r) => r.isAvailability && !r.isRecurring && inWeek(r.start_date))
      .map((r) => ({
        availability_id: r.availability_id,
        type: r.shift_state || "Available",
        start_date: r.start_date,
        end_date: r.end_date,
      }));
    setEntries(sortEntriesOldestFirst(oneTime));
    setLoading(false);
  }, [rows, weekBounds?.weekStartIso, weekBounds?.weekEndIso]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── format ────────────────────────────────────────────────────────────────────
  const fmtEntry = (entry) => {
    const dateStr = formatAppIntlLabel(entry.start_date, {
      month: "short",
      day: "numeric",
    });
    const startT = formatAppTime(entry.start_date, { emptyValue: "" })
      .toLowerCase()
      .replace(":00", "");
    const endT = formatAppTime(entry.end_date, { emptyValue: "" })
      .toLowerCase()
      .replace(":00", "");
    return `${dateStr} · ${startT} – ${endT}`;
  };

  // ── delete ────────────────────────────────────────────────────────────────────
  const handleDelete = async (availId) => {
    setOpenMenuId(null);
    const confirmed = await showConfirm("Remove this availability entry?", "Confirm");
    if (!confirmed) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/availability/${availId}`,
        { method: "DELETE", headers: getAuthHeaders(), credentials: "include" }
      );
      const text = await res.text();
      const j = text ? JSON.parse(text) : { success: res.ok };
      if (!res.ok || !j.success) throw new Error(j.message || "Failed to delete");
      setEntries((prev) => prev.filter((e) => e.availability_id !== availId));
      onSaved();
    } catch (err) {
      showError(err.message || "Failed to delete", "Error");
      console.error("Delete availability error:", err);
    }
  };

  const typeStyle = (t) => {
    const tl = (t || "").toLowerCase();
    if (tl === "available" || tl === "confirmed") return "text-green-600 font-semibold";
    if (tl === "unavailable") return "text-red-400 font-semibold";
    if (tl === "scheduled") return "text-blue-500 font-semibold";
    return "text-gray-400"; // Unconfirmed
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) { setOpenMenuId(null); onClose(); } }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Edit Availability</h2>
            {weekBounds?.weekStart && (
              <p className="text-xs text-gray-400 mt-0.5">
                {formatAppIntlLabel(weekBounds.weekStart, {
                  month: "short",
                  day: "numeric",
                })}
                {" – "}
                {formatAppIntlLabel(weekBounds.weekEnd, {
                  month: "short",
                  day: "numeric",
                })}
              </p>
            )}
          </div>
          <button
            onClick={() => { setShowCreateModal(true); setOpenMenuId(null); }}
            className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded transition-colors"
          >
            CREATE
          </button>
        </div>

        {/* Column headers */}
        <div className="flex items-center px-6 pb-2 border-b border-gray-200">
          <span className="flex-1 text-xs font-medium text-gray-400 uppercase tracking-wide">Date</span>
          <span className="w-28 text-xs font-medium text-gray-400 uppercase tracking-wide">Status</span>
          <span className="w-6" />
        </div>

        {/* Entry list */}
        <div className="flex-1 overflow-y-auto" onClick={() => setOpenMenuId(null)}>
          {loading ? (
            <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              No availability entries yet. Click <strong>CREATE</strong> to add one.
            </div>
          ) : (
            entries.map((entry) => (
              <div
                key={entry.availability_id}
                className="flex items-center px-6 py-4 border-b border-gray-100 hover:bg-gray-50"
              >
                <span className="flex-1 text-sm font-medium text-red-600">
                  {fmtEntry(entry)}
                </span>
                <span className={`w-28 text-sm ${typeStyle(entry.type || "Available")}`}>
                  {entry.type || "Available"}
                </span>

                {/* ⋮ menu */}
                <div className="w-6 relative" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() =>
                      setOpenMenuId(openMenuId === entry.availability_id ? null : entry.availability_id)
                    }
                    className="text-gray-400 hover:text-gray-700 text-lg leading-none px-1"
                  >
                    ⋮
                  </button>
                  {openMenuId === entry.availability_id && (
                    <div className="absolute right-0 top-6 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[110px]">
                      <button
                        onClick={() => { setOpenMenuId(null); setEditEntry(entry); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(entry.availability_id)}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="text-red-600 hover:text-red-700 text-sm font-bold tracking-wide"
          >
            DONE
          </button>
        </div>
      </div>

      {/* Create availability sub-modal */}
      {showCreateModal && (
        <CreateAvailabilityModal
          person={person}
          weekBounds={weekBounds}
          onClose={() => setShowCreateModal(false)}
          onSaved={() => {
            setShowCreateModal(false);
            onSaved();
          }}
        />
      )}

      {/* Edit entry sub-modal */}
      {editEntry && (
        <EditEntryModal
          person={person}
          entry={editEntry}
          onClose={() => setEditEntry(null)}
          onSaved={() => {
            setEditEntry(null);
            onSaved(); // parent load({ soft: true }) refreshes rows
          }}
        />
      )}
    </div>
  );
}

// ─── Edit Entry Modal ─────────────────────────────────────────────────────────
// Pre-filled "Edit Date Availability" popup — same UX as CreateAvailabilityModal
// but uses PUT to update an existing entry.
function EditEntryModal({ person, entry, onClose, onSaved }) {
  // Initialise from existing entry
  const initDT = (ds) => parseDbDateTime(ds) || new Date();
  const [fromDT, setFromDT] = useState(() => initDT(entry.start_date));
  const [toDT, setToDT] = useState(() => initDT(entry.end_date));
  const [status, setStatus] = useState(
    ["Available", "Unavailable", "Confirmed", "Unconfirmed"].includes(entry.type)
      ? entry.type
      : "Confirmed"
  );
  const [activePicker, setActivePicker] = useState(null);
  const [saving, setSaving] = useState(false);

  const CalIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-gray-400 shrink-0">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );

  const fmtDT = (dt) => {
    if (!dt) return null;
    return formatAppDateTime(dt, { emptyValue: "" });
  };

  const toDbDT = (dt) => toDbWallDateTime(dt);

  const handleSave = async () => {
    if (!fromDT || !toDT) { showError("Please select both dates", "Validation"); return; }
    if (fromDT >= toDT) { showError("From Date must be before To Date", "Validation"); return; }
    setSaving(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/availability/${entry.availability_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: JSON.stringify({
            start_date: toDbDT(fromDT),
            end_date: toDbDT(toDT),
            type: status,
          }),
        }
      );
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error(j.message || "Failed to update");
      showSuccess("Availability entry updated!", "Success");
      onSaved();
    } catch (err) {
      showError(err.message || "Failed to update", "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60]">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Edit Date Availability</h3>

          {/* From Date */}
          <div className="mb-1">
            <span className="text-xs text-gray-500">From Date*</span>
            <button
              onClick={() => setActivePicker("from")}
              className="w-full flex items-center gap-3 border-b border-gray-300 py-2 text-left"
            >
              <CalIcon />
              <span className="flex-1 text-sm text-gray-800">{fmtDT(fromDT)}</span>
              <span
                onClick={(e) => { e.stopPropagation(); setFromDT(null); }}
                className="text-gray-400 hover:text-gray-700 text-base leading-none"
              >×</span>
            </button>
          </div>

          <div className="text-sm text-gray-500 px-1 py-2">to</div>

          {/* To Date */}
          <div className="mb-6">
            <span className="text-xs text-gray-500">To Date*</span>
            <button
              onClick={() => setActivePicker("to")}
              className="w-full flex items-center gap-3 border-b border-gray-300 py-2 text-left"
            >
              <CalIcon />
              {toDT
                ? <span className="flex-1 text-sm text-gray-800">{fmtDT(toDT)}</span>
                : <span className="flex-1 text-sm text-gray-400">To Date*</span>
              }
              {toDT && (
                <span
                  onClick={(e) => { e.stopPropagation(); setToDT(null); }}
                  className="text-gray-400 hover:text-gray-700 text-base leading-none"
                >×</span>
              )}
            </button>
          </div>

          {/* Status */}
          <div className="mb-8">
            <p className="text-xs text-gray-500 mb-2">Status*</p>
            <div className="flex items-center gap-6">
              {["Available", "Unavailable"].map((opt) => (
                <label key={opt} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="ee-status"
                    value={opt}
                    checked={status === opt}
                    onChange={() => setStatus(opt)}
                    className="w-4 h-4 accent-red-600"
                  />
                  <span className="text-sm text-gray-700">{opt}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-6">
            <button
              onClick={handleSave}
              disabled={saving || !fromDT || !toDT}
              className={`text-sm font-bold tracking-wide transition-colors
                ${saving || !fromDT || !toDT
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-800 hover:text-red-600"}`}
            >
              {saving ? "SAVING…" : "SAVE"}
            </button>
            <button
              onClick={onClose}
              className="text-sm font-bold tracking-wide text-gray-800 hover:text-gray-600 transition-colors"
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>

      {/* Date+Time Picker (reuse) */}
      {activePicker === "from" && (
        <DateTimePickerPanel
          value={fromDT || new Date()}
          onChange={setFromDT}
          mode="from"
          onContinue={(d) => { setFromDT(d); setActivePicker("to"); }}
          onDone={(d) => { setFromDT(d); setActivePicker(null); }}
          onBack={() => setActivePicker(null)}
        />
      )}
      {activePicker === "to" && (
        <DateTimePickerPanel
          value={toDT || fromDT || new Date()}
          onChange={setToDT}
          mode="to"
          onContinue={(d) => { setToDT(d); setActivePicker(null); }}
          onDone={(d) => { setToDT(d); setActivePicker(null); }}
          onBack={() => setActivePicker("from")}
        />
      )}
    </>
  );
}

// Confirm Availability Modal Component
// Base 30-minute time options for dropdowns.
const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      const ampm = h < 12 ? "AM" : "PM";
      const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const label = `${displayH}:${String(m).padStart(2, "0")} ${ampm}`;
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      opts.push({ label, value });
    }
  }
  return opts;
})();

function toHHMM(datetimeStr) {
  const parts = getAppWallDateParts(parseDbDateTime(datetimeStr));
  if (!parts) return "09:00";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(parts.hour)}:${pad(parts.minute)}`;
}

function toDisplayTime(hhmm) {
  if (!hhmm) return "Select";
  const s = String(hhmm).toLowerCase().trim();
  if (s === "anytime") return "Anytime";
  if (s === "midnight" || s === "23:59") return "Midnight";
  if (s === "nextday") return "7:30 AM";
  if (!s.includes(":")) return s;
  const [hStr, mStr] = s.split(":");
  const h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return s;
  const m = mStr || "00";
  const ampm = h < 12 ? "AM" : "PM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${m} ${ampm}`;
}

const CONFIRM_SPECIAL_TIMES = {
  anytime: { label: "Anytime", hour: 0, minute: 0, sort: -1 },
  midnight: { label: "Midnight", hour: 23, minute: 59, sort: 24 * 60 - 1 },
};

function isConfirmTimeValue(value) {
  return (
    value === "anytime" ||
    value === "midnight" ||
    /^\d{2}:\d{2}$/.test(value)
  );
}

function resolveConfirmTime(value) {
  const special = CONFIRM_SPECIAL_TIMES[value];
  if (special) {
    return { hour: special.hour, minute: special.minute };
  }
  const [hour, minute] = String(value).split(":").map(Number);
  return { hour: hour || 0, minute: minute || 0 };
}
function confirmTimeToSortValue(value) {
  if (CONFIRM_SPECIAL_TIMES[value]) return CONFIRM_SPECIAL_TIMES[value].sort;
  if (!/^\d{2}:\d{2}$/.test(value)) return Number.MAX_SAFE_INTEGER;
  const [h, m] = value.split(":").map(Number);
  return h * 60 + m;
}

function confirmTimeLabel(value) {
  if (CONFIRM_SPECIAL_TIMES[value]) return CONFIRM_SPECIAL_TIMES[value].label;
  return toDisplayTime(value);
}

function toStartSelectValue(datetimeStr) {
  const hhmm = toHHMM(datetimeStr);
  if (hhmm === "00:00") return "anytime";
  return hhmm;
}

function toEndSelectValue(endDatetimeStr, startDatetimeStr) {
  const hhmm = toHHMM(endDatetimeStr);
  if (hhmm === "23:59") return "midnight";
  return hhmm;
}

function buildConfirmTimeOptions(rows, field, isEnd) {
  if (!isEnd) {
    const extras = [{ value: "anytime", label: "Anytime" }];
    const map = new Map(extras.map((o) => [o.value, o]));
    TIME_OPTIONS.filter((o) => o.value !== "00:00").forEach((o) => {
      if (!map.has(o.value)) map.set(o.value, o);
    });
    rows.forEach((r) => {
      const value = r.startHHMM;
      if (isConfirmTimeValue(value) && !map.has(value)) {
        map.set(value, { value, label: confirmTimeLabel(value) });
      }
    });
    return Array.from(map.values()).sort(
      (a, b) => confirmTimeToSortValue(a.value) - confirmTimeToSortValue(b.value),
    );
  }

  const result = [];
  const map = new Map();

  TIME_OPTIONS.filter((o) => o.value !== "00:00").forEach((o) => {
    result.push({ ...o, isNextDay: false });
    map.set(o.value, true);
  });

  result.push({ value: "midnight", label: "Midnight", isNextDay: false });
  map.set("midnight", true);

  result.push({ value: "__HEADER_NEXT_DAY__", label: "Next Day", disabled: true });

  TIME_OPTIONS.filter(
    (o) => o.value !== "00:00" && confirmTimeToSortValue(o.value) <= 12 * 60,
  ).forEach((o) => {
    result.push({ ...o, isNextDay: true });
  });

  rows.forEach((r) => {
    const value = r.endHHMM;
    if (
      value &&
      value !== "nextday" &&
      isConfirmTimeValue(value) &&
      !map.has(value)
    ) {
      result.push({ value, label: confirmTimeLabel(value) });
      map.set(value, true);
    }
  });

  return result;
}

function ConfirmTimeSelect({ value, options, isNextDay, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "center", behavior: "auto" });
    }
  }, [isOpen]);

  const normValue =
    value === "23:59" || value === "23:59:00"
      ? "midnight"
      : value === "00:00" || value === "00:00:00"
        ? "anytime"
        : value;

  const selectedOption =
    options.find(
      (o) =>
        !o.disabled &&
        (o.value === normValue || o.value === value) &&
        (isNextDay ? o.isNextDay === true : o.isNextDay !== true),
    ) ||
    options.find(
      (o) => !o.disabled && (o.value === normValue || o.value === value),
    );

  const displayLabel = selectedOption
    ? selectedOption.label
    : normValue === "nextday"
      ? "7:30 AM"
      : normValue
        ? toDisplayTime(normValue)
        : "Select";

  return (
    <div ref={containerRef} className={`relative min-w-0 flex-1 ${isOpen ? "z-50" : "z-10"}`}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="h-9 w-full flex items-center justify-between rounded-md border border-gray-300 bg-white pl-2.5 pr-2 text-xs text-gray-700 hover:border-gray-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 sm:h-10 sm:pl-3 sm:pr-2.5 sm:text-sm"
      >
        <span className="truncate">{displayLabel}</span>
        <svg
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180 text-red-500" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 max-h-48 w-full min-w-[130px] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-xl z-50">
          {options.map((o, idx) => {
            if (o.disabled) {
              return (
                <div
                  key={`header-${idx}`}
                  className="px-3 py-1.5 text-xs font-semibold text-gray-400 bg-gray-50/80 select-none cursor-default"
                >
                  {o.label}
                </div>
              );
            }
            const isSelected = selectedOption
              ? o === selectedOption
              : o.value === value || (value === "nextday" && o.value === "07:30");
            return (
              <div
                key={`${o.value}-${idx}`}
                ref={isSelected ? selectedRef : null}
                onClick={() => {
                  onChange(o.value, o);
                  setIsOpen(false);
                }}
                className={`px-3 py-1.5 text-xs sm:text-sm cursor-pointer select-none transition-colors ${isSelected
                    ? "font-medium text-red-600 bg-red-50/70"
                    : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                {o.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ConfirmAvailabilityModal({ person, weekBounds, unconfirmedEntries, onClose, onSave }) {
  const {
    weekStart,
    weekEnd,
    weekStartIso: boundsWeekStartIso,
    weekEndIso: boundsWeekEndIso,
  } = weekBounds || {};
  const [saving, setSaving] = useState(false);

  const isNextDay = (startVal, endVal, entry) => {
    if (endVal === "nextday") return true;
    if (endVal === "midnight" || startVal === "anytime") return false;
    if (entry?.start_date && entry?.end_date) {
      const startKey = toAppDateKey(parseDbDateTime(entry.start_date));
      const endKey = toAppDateKey(parseDbDateTime(entry.end_date));
      if (startKey && endKey && endKey > startKey) return true;
    }
    const start = resolveConfirmTime(startVal);
    const end = resolveConfirmTime(endVal);
    return end.hour * 60 + end.minute <= start.hour * 60 + start.minute;
  };

  const initialRows = useMemo(() => {
    const weekStartIso =
      boundsWeekStartIso ||
      getAppWeekStartIso(toAppDateKey(weekStart || new Date()));
    const rows = [];
    for (let i = 0; i < 7; i++) {
      const dateKey = addAppCalendarDays(weekStartIso, i);
      const day = dateFromIso(dateKey);

      const entry = (unconfirmedEntries || []).find((e) => {
        if (!e.start_date) return false;
        return toAppDateKey(parseDbDateTime(e.start_date)) === dateKey;
      });

      const dayParts = getAppWallDateParts(day);
      const isChecked = !!entry;

      rows.push({
        dateKey,
        date: day,
        dayNum: dayParts?.day ?? i + 1,
        dayName: formatAppIntlLabel(day, { weekday: "short" }),
        checked: isChecked,
        hasEntry: !!entry,
        entry,
        startHHMM: entry ? toStartSelectValue(entry.start_date) : "anytime",
        endHHMM: entry ? toEndSelectValue(entry.end_date, entry.start_date) : "midnight",
      });
    }
    return rows;
  }, [boundsWeekStartIso, weekStart, unconfirmedEntries]);

  const [rows, setRows] = useState(initialRows);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const toggleRow = (idx) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, checked: !r.checked } : r));
  };
  const updateTime = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const startTimeOptions = useMemo(
    () => buildConfirmTimeOptions(rows, "start", false),
    [rows],
  );
  const endTimeOptions = useMemo(
    () => buildConfirmTimeOptions(rows, "end", true),
    [rows],
  );

  const conflictRowMap = useMemo(() => {
    const map = new Map();
    const ranges = rows
      .map((r, idx) => {
        if (!r.checked) return null;
        if (!r.dateKey) return null;
        const [y, m, d] = r.dateKey.split("-").map(Number);
        if (!y || !m || !d) return null;

        const start = resolveConfirmTime(r.startHHMM);
        const end = resolveConfirmTime(r.endHHMM);

        const startMs = new Date(y, m - 1, d, start.hour, start.minute, 0, 0).getTime();

        const startMin = start.hour * 60 + start.minute;
        const endMin = end.hour * 60 + end.minute;
        const nextDay = isNextDay(r.startHHMM, r.endHHMM, r.entry);
        const endDay = nextDay ? d + 1 : d;
        const endMs = new Date(y, m - 1, endDay, end.hour, end.minute, 0, 0).getTime();

        return { idx, startMs, endMs };
      })
      .filter(Boolean);

    for (let i = 0; i < ranges.length; i++) {
      for (let j = i + 1; j < ranges.length; j++) {
        const a = ranges[i];
        const b = ranges[j];
        if (a.startMs < b.endMs && b.startMs < a.endMs) {
          map.set(a.idx, true);
          map.set(b.idx, true);
        }
      }
    }

    return map;
  }, [rows]);

  const fmtWeek = (d) =>
    formatAppIntlLabel(d, { month: "short", day: "numeric" }) || "";
  const displayWeekStart = boundsWeekStartIso
    ? dateFromIso(boundsWeekStartIso)
    : weekStart;
  const displayWeekEnd = boundsWeekEndIso
    ? dateFromIso(boundsWeekEndIso)
    : weekEnd;

  const handleConfirm = async () => {
    if (conflictRowMap && conflictRowMap.size > 0) {
      showError("Please review errors highlighted in red", "Error");
      return;
    }
    const entries = rows
      .filter((r) => r.hasEntry || r.checked)
      .map((r) => {
        const start = resolveConfirmTime(r.startHHMM);
        const end = resolveConfirmTime(r.endHHMM);
        const startMin = start.hour * 60 + start.minute;
        const endMin = end.hour * 60 + end.minute;
        const endDateKey =
          r.endHHMM === "nextday" ||
            (r.endHHMM !== "midnight" && endMin <= startMin)
            ? addAppCalendarDays(r.dateKey, 1)
            : r.dateKey;

        return {
          start_date: wallDateTimeToDb(r.dateKey, start.hour, start.minute),
          end_date: wallDateTimeToDb(endDateKey, end.hour, end.minute),
          type: r.checked ? "Available" : "Unavailable",
        };
      });

    setSaving(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/accounts/${person.id}/availabilities/bulk-create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ entries }),
        },
      );
      const j = await res.json();
      if (!res.ok || !j.success) {
        throw new Error(j.message || "Failed to confirm availability");
      }
      showSuccess("Availability confirmed successfully!", "Success");
      onSave?.(entries);
      onClose();
    } catch (err) {
      showError(err.message || "Failed to confirm availability", "Error");
    } finally {
      setSaving(false);
    }
  };

  const availableCount = rows.filter((r) => r.checked).length;
  const unavailableCount = rows.filter((r) => r.hasEntry && !r.checked).length;

  return (
    <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/50 p-3 sm:p-4">
      <div className="flex max-h-[min(90vh,640px)] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="px-4 pt-5 pb-3 sm:px-6 sm:pt-6">
          <h2 className="text-xl font-semibold text-gray-900">Confirm Availability</h2>
          <p className="mt-1 text-xs text-gray-500 sm:text-sm">
            Check the days {person?.name || `#${person?.id}`} is available for{" "}
            <span className="font-medium text-gray-700">
              {fmtWeek(displayWeekStart)} - {fmtWeek(displayWeekEnd)}
            </span>
          </p>
        </div>

        {/* Day rows */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 pb-4 sm:px-6">
          <div className="space-y-3">
            {rows.map((r, idx) => {
              const nextDayFlag = isNextDay(r.startHHMM, r.endHHMM, r.entry);
              const hasConflict = conflictRowMap.has(idx);

              return (
                <div
                  key={r.dateKey}
                  className="flex items-start gap-2 min-h-[44px] sm:items-center sm:gap-3"
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={r.checked}
                    onChange={() => toggleRow(idx)}
                    className="mt-2.5 h-4 w-4 flex-shrink-0 accent-red-600 sm:mt-0"
                  />

                  {/* Date badge */}
                  <div className="flex w-24 flex-shrink-0 items-center gap-1.5">
                    <span className="text-xs font-semibold text-gray-900 sm:text-sm">
                      {r.dayName} {r.dayNum}
                    </span>
                    {r.hasEntry && (
                      <span className="inline-flex shrink-0 items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600" title="Existing unconfirmed pattern">
                        Preset
                      </span>
                    )}
                  </div>

                  {/* Time selectors (only editable when checked) */}
                  {r.checked ? (
                    <div className="flex flex-1 items-start gap-1.5 sm:gap-2">
                      <div className="flex min-w-0 flex-1 flex-col">
                        <ConfirmTimeSelect
                          value={r.startHHMM}
                          options={startTimeOptions}
                          onChange={(val) => updateTime(idx, "startHHMM", val)}
                        />
                        {hasConflict && (
                          <div className="mt-1 pl-0.5 text-[11px] font-semibold text-red-600">
                            Conflicting time slot
                          </div>
                        )}
                      </div>

                      <span className="shrink-0 pt-2 text-xs leading-none text-gray-500 sm:text-sm">
                        to
                      </span>

                      <div className="flex min-w-0 flex-1 flex-col">
                        <ConfirmTimeSelect
                          value={r.endHHMM}
                          options={endTimeOptions}
                          onChange={(val) => updateTime(idx, "endHHMM", val)}
                        />
                        {nextDayFlag && (
                          <div className="mt-1 pl-0.5 text-[11px] font-semibold text-gray-400">
                            Next Day
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-1 items-center justify-between py-2 text-xs text-gray-400 italic">
                      <span>Not available</span>
                      {r.hasEntry && (
                        <span className="text-[11px] text-red-500 font-normal not-italic">Will be set Unavailable</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50/50 px-4 py-3 sm:px-6">
          <div className="text-xs text-gray-600">
            <span className="font-semibold text-green-700">{availableCount} Available</span>
            {unavailableCount > 0 && (
              <span className="ml-2 font-semibold text-red-600">· {unavailableCount} Unavailable</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={saving}
              className={`text-sm font-semibold transition-colors ${saving
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-red-600 hover:text-red-700"
                }`}
            >
              {saving ? "CONFIRMING…" : "CONFIRM"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-semibold text-gray-500 hover:text-gray-700"
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Ongoing Availability Modal Component
function OngoingAvailabilityModal({ person, availability, onClose, onSave }) {
  const [localAvailability, setLocalAvailability] = useState(availability);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalAvailability(availability);
  }, [availability]);

  const days = [
    { key: "monday", label: "Monday" },
    { key: "tuesday", label: "Tuesday" },
    { key: "wednesday", label: "Wednesday" },
    { key: "thursday", label: "Thursday" },
    { key: "friday", label: "Friday" },
    { key: "saturday", label: "Saturday" },
    { key: "sunday", label: "Sunday" },
  ];

  const toggleDay = (dayKey) => {
    setLocalAvailability((prev) => {
      const dayData = prev[dayKey] || {};
      const enabled = !dayData.enabled;
      const times = dayData.times && dayData.times.length ? dayData.times : [{ start: "09:00", end: "17:00" }];
      return {
        ...prev,
        [dayKey]: {
          ...dayData,
          enabled,
          times,
        },
      };
    });
  };

  const updateTime = (dayKey, slotIndex, field, value, selectedOption) => {
    setLocalAvailability((prev) => {
      const dayData = prev[dayKey] || { enabled: true, times: [] };
      const newTimes = [...(dayData.times || [])];
      const prevSlot = newTimes[slotIndex] || {};
      const current = { ...prevSlot, [field]: value };
      if (field === "start" || field === "end") {
        if (field === "end" && selectedOption && typeof selectedOption.isNextDay === "boolean") {
          current.nextDay = selectedOption.isNextDay;
        } else {
          const startMin = parseTimeInputToMinutes(current.start);
          const endMin = parseTimeInputToMinutes(current.end);
          if (endMin <= startMin && current.end !== "midnight" && current.start !== "anytime") {
            current.nextDay = true;
          } else if (field === "start" && prevSlot.nextDay && current.end !== "midnight" && current.start !== "anytime") {
            current.nextDay = true;
          } else if (field === "end" && value !== "nextday") {
            current.nextDay = false;
          }
        }
      }
      newTimes[slotIndex] = current;
      return {
        ...prev,
        [dayKey]: {
          ...dayData,
          times: newTimes,
        },
      };
    });
  };

  const addTimeSlot = (dayKey) => {
    setLocalAvailability((prev) => {
      const dayData = prev[dayKey] || { enabled: true, times: [] };
      return {
        ...prev,
        [dayKey]: {
          ...dayData,
          enabled: true,
          times: [...(dayData.times || []), { start: "09:00", end: "17:00" }],
        },
      };
    });
  };

  const removeTimeSlot = (dayKey, slotIndex) => {
    setLocalAvailability((prev) => {
      const dayData = prev[dayKey] || { enabled: true, times: [] };
      const newTimes = (dayData.times || []).filter((_, idx) => idx !== slotIndex);
      return {
        ...prev,
        [dayKey]: {
          ...dayData,
          enabled: newTimes.length > 0 ? dayData.enabled : false,
          times: newTimes.length > 0 ? newTimes : [{ start: "09:00", end: "17:00" }],
        },
      };
    });
  };

  const allSlotsRows = useMemo(() => {
    const list = [];
    Object.values(localAvailability || {}).forEach((dayData) => {
      if (!dayData || !dayData.enabled || !Array.isArray(dayData.times)) return;
      dayData.times.forEach((t) => {
        list.push({ startHHMM: t.start || "09:00", endHHMM: t.end || "17:00" });
      });
    });
    return list;
  }, [localAvailability]);

  const startTimeOptions = useMemo(
    () => buildConfirmTimeOptions(allSlotsRows, "start", false),
    [allSlotsRows],
  );
  const endTimeOptions = useMemo(
    () => buildConfirmTimeOptions(allSlotsRows, "end", true),
    [allSlotsRows],
  );

  const conflictMap = useMemo(() => {
    const map = new Map();
    const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
    const ranges = [];

    dayKeys.forEach((dayKey, dayIdx) => {
      const dayData = localAvailability?.[dayKey];
      if (!dayData || !dayData.enabled || !Array.isArray(dayData.times)) return;

      dayData.times.forEach((t, slotIdx) => {
        const startVal = t.start || "09:00";
        const endVal = t.end || "17:00";

        const start = resolveConfirmTime(startVal);
        const end = resolveConfirmTime(endVal);

        const startMin = start.hour * 60 + start.minute;
        const endMin = end.hour * 60 + end.minute;

        const nextDay =
          endVal === "nextday" ||
          (endVal !== "midnight" && startVal !== "anytime" && endMin <= startMin) ||
          !!t.nextDay;

        const dayBaseMin = dayIdx * 24 * 60;
        const startMs = dayBaseMin + startMin;
        const endMs = dayBaseMin + (nextDay ? 24 * 60 + endMin : endMin);

        const key = `${dayKey}-${slotIdx}`;
        ranges.push({ key, startMs, endMs });

        if (endMs > 7 * 24 * 60) {
          ranges.push({ key, startMs: startMs - 7 * 24 * 60, endMs: endMs - 7 * 24 * 60 });
        }
      });
    });

    for (let i = 0; i < ranges.length; i++) {
      for (let j = i + 1; j < ranges.length; j++) {
        const a = ranges[i];
        const b = ranges[j];
        if (a.startMs < b.endMs && b.startMs < a.endMs) {
          map.set(a.key, true);
          map.set(b.key, true);
        }
      }
    }

    return map;
  }, [localAvailability]);

  const handleSave = async () => {
    if (saving) return;
    if (conflictMap && conflictMap.size > 0) {
      showError("Please review errors highlighted in red", "Error");
      return;
    }
    setSaving(true);
    try {
      await onSave(localAvailability);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/50 p-3 sm:p-4">
      <div className="flex max-h-[min(90vh,640px)] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="px-4 pt-5 pb-3 sm:px-6 sm:pt-6">
          <h2 className="text-xl font-semibold text-gray-900">Ongoing Availability</h2>
        </div>

        {/* Day rows */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 pb-4 sm:px-6">
          <div className="space-y-3">
            {days.map(({ key, label }) => {
              const dayData = localAvailability?.[key] || { enabled: false, times: [] };
              return (
                <div key={key} className="flex items-start gap-2 min-h-[44px] sm:items-center sm:gap-3">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={!!dayData.enabled}
                    onChange={() => toggleDay(key)}
                    className="mt-2.5 h-4 w-4 flex-shrink-0 accent-red-600 sm:mt-0"
                  />

                  {/* Day name */}
                  <span className="w-24 flex-shrink-0 text-sm font-medium text-gray-800">{label}</span>

                  {/* Time slots */}
                  {dayData.enabled && (
                    <div className="flex-1 space-y-2">
                      {(dayData.times || []).map((timeSlot, idx) => {
                        const isNextDay =
                          timeSlot.nextDay ||
                          timeSlot.end === "nextday" ||
                          (timeSlot.end !== "midnight" &&
                            timeSlot.start !== "anytime" &&
                            resolveConfirmTime(timeSlot.end).hour * 60 + resolveConfirmTime(timeSlot.end).minute <=
                            resolveConfirmTime(timeSlot.start).hour * 60 + resolveConfirmTime(timeSlot.start).minute);

                        const hasConflict = conflictMap.has(`${key}-${idx}`);

                        return (
                          <div key={idx} className="flex items-start gap-1.5 sm:gap-2">
                            <div className="flex min-w-0 flex-1 flex-col">
                              <ConfirmTimeSelect
                                value={timeSlot.start || "09:00"}
                                options={startTimeOptions}
                                onChange={(val, opt) => updateTime(key, idx, "start", val, opt)}
                              />
                              {hasConflict && (
                                <div className="mt-1 pl-0.5 text-[11px] font-semibold text-red-600">
                                  Conflicting time slot
                                </div>
                              )}
                            </div>

                            <span className="shrink-0 pt-2 text-xs leading-none text-gray-500 sm:text-sm">to</span>

                            <div className="flex min-w-0 flex-1 flex-col">
                              <ConfirmTimeSelect
                                value={timeSlot.end || "17:00"}
                                options={endTimeOptions}
                                isNextDay={isNextDay}
                                onChange={(val, opt) => updateTime(key, idx, "end", val, opt)}
                              />
                              {isNextDay && (
                                <div className="mt-1 pl-0.5 text-[11px] font-semibold text-gray-400">
                                  Next Day
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-1 shrink-0 pt-1.5 sm:pt-1">
                              {dayData.times.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeTimeSlot(key, idx)}
                                  className="text-gray-400 hover:text-red-600 text-base font-semibold px-1"
                                  title="Remove time slot"
                                >
                                  ×
                                </button>
                              )}
                              {idx === dayData.times.length - 1 && (
                                <button
                                  type="button"
                                  onClick={() => addTimeSlot(key)}
                                  className="text-gray-600 hover:text-gray-900 font-bold text-lg px-1 leading-none"
                                  title="Add time slot"
                                >
                                  +
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-3 border-t border-gray-200 p-4">
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="rounded bg-red-600 px-6 py-2 text-xs font-medium tracking-wide text-white hover:bg-red-700 disabled:opacity-50 sm:text-sm"
          >
            {saving ? "SAVING..." : "SAVE"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 bg-white px-6 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 sm:text-sm"
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}
